import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  MovieFilter as ProgramsIcon,
  PersonAdd as PersonAddIcon,
  DateRange as WeekPlanIcon,
  Inbox as InboxIcon,
  WarningAmber as WarningIcon,
  PersonOff as AssigneeConflictIcon
} from '@mui/icons-material';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { lt } from 'date-fns/locale';
import { API_ENDPOINTS, getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import PlanetariumActivityLog from './PlanetariumActivityLog';
import { formatMonthYearLt } from '../utils/ltDateFormat';

type PlanetariumSection = 'calendar' | 'logs';

type SessionStatus =
  | 'planned'
  | 'planned_private'
  | 'confirmed_private'
  | 'not_working'
  | 'technical_day'
  | 'planetarium_closed';

interface Colleague {
  id: string;
  firstName: string;
  lastName: string;
  department?: string;
}

interface PlanetariumProgram {
  id: string;
  name: string;
  isActive: boolean;
  color?: string | null;
}

type RequestType = 'private_existing' | 'private_new' | 'program_change';

interface PrivateRequest {
  id: string;
  requestType?: RequestType;
  sessionId?: string | null;
  sessionDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  program?: string | null;
  requestedProgram?: string | null;
  requestedStatus?: SessionStatus | null;
  requestedLanguage?: string | null;
  requestedAudience?: string | null;
  clientContacts?: string | null;
  comments?: string | null;
  status: 'pending' | 'accepted' | 'denied';
  reviewNote?: string | null;
  session?: {
    id: string;
    sessionDate: string;
    startTime: string;
    endTime?: string | null;
    program: string;
    status: SessionStatus;
    audience?: string | null;
    language?: string | null;
    clientContacts?: string | null;
    comments?: string | null;
  };
  requester?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface RequestBaseline {
  status: SessionStatus;
  program: string;
  audience: string;
  language: string;
}

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  private_existing: 'Privatus',
  private_new: 'Naujas laikas',
  program_change: 'Seansas'
};

const PENDING_REQUEST_CHIP_SX = {
  border: '2.5px solid rgba(255, 196, 77, 0.95)',
  boxShadow: '0 0 0 2px rgba(255, 180, 60, 0.35), 0 0 14px rgba(255, 190, 80, 0.45)'
};

interface AssigneeScheduleConflictPerson {
  userId: string;
  firstName: string;
  lastName: string;
  scheduleStatus: string | null;
  scheduleApproved: boolean;
}

interface AssigneeScheduleConflict {
  sessionId: string;
  sessionDate: string;
  startTime?: string | null;
  program?: string | null;
  assignees: AssigneeScheduleConflictPerson[];
}

interface PlanetariumSession {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime?: string | null;
  program: string;
  status: SessionStatus;
  audience?: string | null;
  language?: string | null;
  clientContacts?: string | null;
  comments?: string | null;
  assignedColleagues?: Colleague[];
}

interface SessionFormState {
  id?: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  program: string;
  status: SessionStatus;
  audience: string;
  language: string;
  clientContacts: string;
  comments: string;
  assigneeIds: string[];
  allDay: boolean;
}

const STATUS_OPTIONS: { value: SessionStatus; label: string }[] = [
  { value: 'planned', label: 'Planuojamas' },
  { value: 'planned_private', label: 'Planuojamas Privatus' },
  { value: 'confirmed_private', label: 'Patvirtintas Privatus' },
  { value: 'not_working', label: 'Nedirbame' },
  { value: 'technical_day', label: 'Techninė diena' },
  { value: 'planetarium_closed', label: 'Planetariumas uždarytas' }
];

const REQUESTABLE_SESSION_STATUSES: SessionStatus[] = [
  'planned',
  'planned_private',
  'confirmed_private'
];

const ALL_DAY_START = '00:00';
const ALL_DAY_END = '23:59';
const COPIED_DAY_PLAN_KEY = 'planetarium.copiedDayPlan';
/** ~4 normal session chip rows */
const FULL_DAY_TECH_TILE_MIN_HEIGHT = 112;

interface CopiedDaySession {
  startTime: string;
  endTime?: string | null;
  program: string;
  status: SessionStatus;
  audience?: string | null;
  language?: string | null;
  clientContacts?: string | null;
  comments?: string | null;
  assigneeIds: string[];
}

function isDayBlockStatus(status: SessionStatus) {
  return (
    status === 'not_working' ||
    status === 'technical_day' ||
    status === 'planetarium_closed'
  );
}

function isFullDayTechnicalSession(session: PlanetariumSession) {
  return (
    (session.status === 'technical_day' || session.status === 'planetarium_closed') &&
    isAllDayTimes(session.startTime, session.endTime)
  );
}

function sessionToCopiedPayload(session: PlanetariumSession): CopiedDaySession {
  return {
    startTime: timeShort(session.startTime),
    endTime: timeShort(session.endTime) || null,
    program: session.program || '',
    status: session.status,
    audience: session.audience || null,
    language: session.language || null,
    clientContacts: session.clientContacts || null,
    comments: session.comments || null,
    assigneeIds: (session.assignedColleagues || []).map((c) => c.id)
  };
}

function loadCopiedDayPlan(): CopiedDaySession[] {
  try {
    const raw = sessionStorage.getItem(COPIED_DAY_PLAN_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistCopiedDayPlan(items: CopiedDaySession[]) {
  try {
    sessionStorage.setItem(COPIED_DAY_PLAN_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota */
  }
}

/** Distinct soft palette for programs (stable via hash). */
const PROGRAM_COLOR_PALETTE = [
  '#90caf9',
  '#a5d6a7',
  '#ce93d8',
  '#ffcc80',
  '#80cbc4',
  '#ef9a9a',
  '#9fa8da',
  '#fff59d',
  '#80deea',
  '#f48fb1',
  '#bcaaa4',
  '#c5e1a5',
  '#b39ddb',
  '#ffe082',
  '#81d4fa',
  '#e6ee9c'
];

const STATUS_OVERRIDE_COLORS: Partial<Record<SessionStatus, string>> = {
  not_working: 'rgba(180, 190, 210, 0.35)',
  technical_day: 'rgba(255, 220, 120, 0.28)',
  planetarium_closed: 'rgba(110, 125, 155, 0.38)',
  planned_private: 'rgba(156, 120, 255, 0.42)',
  confirmed_private: 'rgba(72, 200, 140, 0.42)'
};

const PRIVATE_STATUS_CHIP: Partial<
  Record<SessionStatus, { label: string; bgcolor: string; color: string }>
> = {
  planned_private: {
    label: 'Plan. priv.',
    bgcolor: 'rgba(179, 136, 255, 0.95)',
    color: '#041820'
  },
  confirmed_private: {
    label: 'Patv. priv.',
    bgcolor: 'rgba(105, 240, 174, 0.95)',
    color: '#041820'
  }
};

const DEFAULT_LANGUAGE = 'LT';

const WEEKLY_PLAN_STORAGE_KEY = 'planetarium.weeklyPlan';

const WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Pr' },
  { value: 2, label: 'An' },
  { value: 3, label: 'Tr' },
  { value: 4, label: 'Kt' },
  { value: 5, label: 'Pn' },
  { value: 6, label: 'Št' },
  { value: 7, label: 'Sk' }
];

type WeeklyPlanSlotStatus = 'planned' | 'technical_day' | 'planetarium_closed';

interface WeeklyPlanSlot {
  id: string;
  startTime: string;
  durationMinutes: number;
  program: string;
  language: string;
  /** planned session times, or full-day Techninė diena / Planetariumas uždarytas */
  status: WeeklyPlanSlotStatus;
}

interface WeeklyPlanState {
  version: 2;
  activeWeekday: number;
  byWeekday: Record<string, WeeklyPlanSlot[]>;
}

function newSlotId() {
  return `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function defaultWeeklySlot(startTime = '11:00'): WeeklyPlanSlot {
  return {
    id: newSlotId(),
    startTime,
    durationMinutes: 60,
    program: '',
    language: DEFAULT_LANGUAGE,
    status: 'planned'
  };
}

function defaultTechnicalDaySlot(): WeeklyPlanSlot {
  return {
    id: newSlotId(),
    startTime: ALL_DAY_START,
    durationMinutes: 24 * 60 - 1,
    program: '',
    language: DEFAULT_LANGUAGE,
    status: 'technical_day'
  };
}

function defaultPlanetariumClosedSlot(): WeeklyPlanSlot {
  return {
    id: newSlotId(),
    startTime: ALL_DAY_START,
    durationMinutes: 24 * 60 - 1,
    program: '',
    language: DEFAULT_LANGUAGE,
    status: 'planetarium_closed'
  };
}

function defaultDaySlots(): WeeklyPlanSlot[] {
  return [
    defaultWeeklySlot('11:00'),
    defaultWeeklySlot('13:00'),
    defaultWeeklySlot('15:00'),
    defaultWeeklySlot('17:00')
  ];
}

function cloneSlots(slots: WeeklyPlanSlot[]): WeeklyPlanSlot[] {
  return slots.map((s) => ({ ...s, id: newSlotId() }));
}

function normalizeSlot(s: Partial<WeeklyPlanSlot> | null | undefined): WeeklyPlanSlot | null {
  if (!s) return null;
  const status: WeeklyPlanSlotStatus =
    s.status === 'technical_day'
      ? 'technical_day'
      : s.status === 'planetarium_closed'
        ? 'planetarium_closed'
        : 'planned';
  if (status === 'technical_day' || status === 'planetarium_closed') {
    return {
      id: s.id || newSlotId(),
      startTime: ALL_DAY_START,
      durationMinutes: 24 * 60 - 1,
      program: String(s.program || ''),
      language: String(s.language || DEFAULT_LANGUAGE) || DEFAULT_LANGUAGE,
      status
    };
  }
  const startTime = String(s.startTime || '').slice(0, 5);
  if (!startTime) return null;
  return {
    id: s.id || newSlotId(),
    startTime,
    durationMinutes:
      Number.isFinite(Number(s.durationMinutes)) && Number(s.durationMinutes) >= 5
        ? Math.min(12 * 60, Number(s.durationMinutes))
        : 60,
    program: String(s.program || ''),
    language: String(s.language || DEFAULT_LANGUAGE) || DEFAULT_LANGUAGE,
    status: 'planned'
  };
}

/** Monday = Techninė diena; Tue–Sun with 4 default times */
function defaultWeeklyPlan(): WeeklyPlanState {
  const byWeekday: Record<string, WeeklyPlanSlot[]> = {
    '1': [defaultTechnicalDaySlot()]
  };
  for (let d = 2; d <= 7; d += 1) {
    byWeekday[String(d)] = defaultDaySlots();
  }
  return {
    version: 2,
    activeWeekday: 1,
    byWeekday
  };
}

function loadWeeklyPlan(): WeeklyPlanState {
  try {
    const raw = localStorage.getItem(WEEKLY_PLAN_STORAGE_KEY);
    if (!raw) return defaultWeeklyPlan();
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    // v2
    if (parsed.version === 2 && parsed.byWeekday && typeof parsed.byWeekday === 'object') {
      const byWeekday: Record<string, WeeklyPlanSlot[]> = {};
      for (let d = 1; d <= 7; d += 1) {
        const key = String(d);
        const list = (parsed.byWeekday as Record<string, unknown>)[key];
        byWeekday[key] = Array.isArray(list)
          ? list.map((s) => normalizeSlot(s as Partial<WeeklyPlanSlot>)).filter(Boolean) as WeeklyPlanSlot[]
          : [];
      }
      const active = Number(parsed.activeWeekday);
      return {
        version: 2,
        activeWeekday: active >= 1 && active <= 7 ? active : 2,
        byWeekday
      };
    }

    // v1 migrate: shared weekdays + slots → copy onto each selected day
    const weekdays = Array.isArray(parsed.weekdays)
      ? [...new Set((parsed.weekdays as unknown[]).map((d) => Number(d)).filter((d) => d >= 1 && d <= 7))]
      : [];
    const slots = Array.isArray(parsed.slots)
      ? ((parsed.slots as Partial<WeeklyPlanSlot>[])
          .map((s) => normalizeSlot(s))
          .filter(Boolean) as WeeklyPlanSlot[])
      : [];
    const byWeekday: Record<string, WeeklyPlanSlot[]> = {};
    for (let d = 1; d <= 7; d += 1) byWeekday[String(d)] = [];
    const targets = weekdays.length ? weekdays : [2, 3, 4, 5, 6, 7];
    for (const d of targets) {
      byWeekday[String(d)] = slots.length ? cloneSlots(slots) : defaultDaySlots();
    }
    return {
      version: 2,
      activeWeekday: targets[0] || 2,
      byWeekday
    };
  } catch {
    return defaultWeeklyPlan();
  }
}

function saveWeeklyPlan(plan: WeeklyPlanState) {
  try {
    localStorage.setItem(WEEKLY_PLAN_STORAGE_KEY, JSON.stringify(plan));
  } catch {
    /* ignore */
  }
}

function weeklyPlanHasSlots(plan: WeeklyPlanState): boolean {
  return Object.values(plan.byWeekday).some((slots) =>
    slots.some(
      (s) =>
        s.status === 'technical_day' ||
        s.status === 'planetarium_closed' ||
        Boolean(s.startTime)
    )
  );
}

function activeDaySlots(plan: WeeklyPlanState): WeeklyPlanSlot[] {
  return plan.byWeekday[String(plan.activeWeekday)] || [];
}

/** Design tokens — mission-console glass on night sky */
/** Design tokens — light page surfaces */
const TOKENS = {
  ink: '#111111',
  muted: 'rgba(17, 17, 17, 0.65)',
  faint: 'rgba(17, 17, 17, 0.45)',
  line: 'rgba(17, 17, 17, 0.16)',
  accent: '#3DB8C5',
  accentHover: '#2FA3B0',
  warnFill: 'rgba(255, 180, 80, 0.18)',
  warnBorder: 'rgba(200, 130, 40, 0.55)',
  warnInk: '#8A4B00',
  dangerFill: 'rgba(255, 90, 90, 0.12)',
  dangerBorder: 'rgba(200, 60, 60, 0.45)',
  okFill: 'rgba(80, 200, 140, 0.14)',
  okBorder: 'rgba(40, 140, 90, 0.45)',
  conflictFill: 'rgba(176, 0, 120, 0.1)',
  conflictBorder: 'rgba(176, 0, 120, 0.85)',
  conflictInk: '#7A0054',
  conflictChip: 'rgba(255, 40, 160, 0.95)',
  radius: 2
} as const;

const SCHEDULE_STATUS_LT: Record<string, string> = {
  working: 'Dirba',
  day_off: 'Atostogos',
  mothers_day: 'Mamadienis/Tėvadienis',
  remote: 'Dirba Nuotoliu',
  sick_leave: 'Ligos atostogos',
  rest_day: 'Poilsio diena',
  state_holiday: 'Valstybinė šventė',
  business_trip: 'Komandiruotė',
  military_service: 'Karo tarnyba',
  praktika: 'Praktika'
};

function scheduleStatusLabel(status: string | null | undefined, approved?: boolean): string {
  if (!status) return 'nėra įrašo grafike';
  const label = SCHEDULE_STATUS_LT[status] || status;
  if (approved === false) return `${label} (nepatvirtinta)`;
  return label;
}

const GLASS_SX = {
  bgcolor: '#ffffff',
  border: `1px solid ${TOKENS.line}`,
  boxShadow: 'none',
  borderRadius: TOKENS.radius
} as const;

const GLASS_CALENDAR_SX = {
  ...GLASS_SX,
  bgcolor: '#ffffff'
} as const;

const BTN_GHOST_SX = {
  textTransform: 'none' as const,
  fontWeight: 600,
  color: TOKENS.ink,
  borderColor: TOKENS.line,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(17, 17, 17, 0.06)',
    borderColor: 'rgba(17, 17, 17, 0.28)'
  }
};

const BTN_PRIMARY_SX = {
  textTransform: 'none' as const,
  fontWeight: 600,
  bgcolor: TOKENS.accent,
  color: '#041820',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: TOKENS.accentHover,
    boxShadow: 'none'
  }
};

const FIELD_SX = {
  bgcolor: '#ffffff',
  borderRadius: 1,
  color: TOKENS.ink,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: TOKENS.line
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(17, 17, 17, 0.28)'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: TOKENS.accent
  },
  '& .MuiSelect-icon': { color: TOKENS.muted }
};

const CHIP_BASE_SX = {
  fontWeight: 600,
  height: 28,
  bgcolor: 'rgba(17, 17, 17, 0.06)',
  borderColor: TOKENS.line,
  color: TOKENS.ink,
  '&:hover': { bgcolor: 'rgba(17, 17, 17, 0.1)' }
};

const DAY_ACTION_SX = {
  minWidth: 28,
  height: 24,
  px: 0.6,
  py: 0,
  fontSize: '0.72rem',
  fontWeight: 700,
  lineHeight: 1,
  textTransform: 'none' as const,
  borderRadius: 1,
  boxShadow: 'none'
};

const ALERT_BASE_SX = {
  ...GLASS_SX,
  mb: 2,
  alignItems: 'flex-start',
  color: TOKENS.ink,
  '& .MuiAlert-icon': { opacity: 0.95, mt: 0.25 },
  '& .MuiAlert-message': { width: '100%', color: TOKENS.ink },
  '& .MuiAlert-action .MuiIconButton-root': { color: TOKENS.muted }
} as const;

const DIALOG_PAPER_SX = {
  bgcolor: '#ffffff',
  border: `1px solid ${TOKENS.line}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  backgroundImage: 'none',
  color: TOKENS.ink,
  borderRadius: 2
} as const;

const DIALOG_SX = {
  '& .MuiDialogTitle-root': {
    color: TOKENS.ink,
    fontWeight: 700,
    letterSpacing: '0.02em',
    borderBottom: `1px solid ${TOKENS.line}`,
    pb: 1.5
  },
  '& .MuiDialogContent-root': {
    color: TOKENS.ink,
    pt: '16px !important'
  },
  '& .MuiDialogActions-root': {
    borderTop: `1px solid ${TOKENS.line}`,
    px: 2.5,
    py: 1.5
  },
  '& .MuiInputLabel-root': { color: TOKENS.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: TOKENS.accent },
  '& .MuiFormHelperText-root': { color: TOKENS.faint },
  '& .MuiOutlinedInput-root': {
    color: TOKENS.ink,
    bgcolor: '#ffffff',
    '& fieldset': { borderColor: TOKENS.line },
    '&:hover fieldset': { borderColor: 'rgba(17, 17, 17, 0.28)' },
    '&.Mui-focused fieldset': { borderColor: TOKENS.accent }
  },
  '& input, & textarea': { colorScheme: 'light' },
  '& .MuiSelect-icon': { color: TOKENS.muted },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: TOKENS.muted
  },
  '& .MuiChip-root': {
    bgcolor: 'rgba(17, 17, 17, 0.06)',
    color: TOKENS.ink,
    border: `1px solid ${TOKENS.line}`
  },
  '& .MuiListItemText-primary': { color: TOKENS.ink },
  '& .MuiListItemText-secondary': { color: TOKENS.muted },
  '& .MuiIconButton-root': { color: TOKENS.muted },
  '& .MuiCheckbox-root': { color: TOKENS.line },
  '& .MuiCheckbox-root.Mui-checked': { color: TOKENS.accent },
  '& .MuiFormControlLabel-label': { color: TOKENS.ink }
} as const;

const DIALOG_BACKDROP_SX = {
  bgcolor: 'rgba(0, 0, 0, 0.4)'
} as const;

const MENU_PAPER_SX = {
  bgcolor: '#ffffff',
  color: TOKENS.ink,
  border: `1px solid ${TOKENS.line}`,
  backgroundImage: 'none',
  '& .MuiMenuItem-root.Mui-selected': {
    bgcolor: 'rgba(61, 184, 197, 0.18)'
  },
  '& .MuiMenuItem-root:hover': {
    bgcolor: 'rgba(17, 17, 17, 0.06)'
  },
  '& .MuiAutocomplete-option[aria-selected="true"]': {
    bgcolor: 'rgba(61, 184, 197, 0.18)'
  },
  '& .MuiAutocomplete-option:hover': {
    bgcolor: 'rgba(17, 17, 17, 0.06)'
  }
} as const;

const TIME_INPUT_PLACEHOLDER = 'HH:MM';

/** Normalize typed values like 20:00, 9:30, 2000 → HH:MM. */
function normalizeTypedTime(raw: string): string | null {
  const s = String(raw || '')
    .trim()
    .replace('.', ':');
  if (!s) return null;
  let match = s.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) match = s.match(/^(\d{1,2})(\d{2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const min = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function sanitizeTimeTyping(raw: string): string {
  return String(raw || '')
    .replace(/[^\d:.]/g, '')
    .replace('.', ':')
    .slice(0, 5);
}

type TimeInputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: object;
};

/** Plain manual HH:MM entry (no picker / no suggestions). */
function TimeInput({ label, value, onChange, disabled, fullWidth, size, sx }: TimeInputProps) {
  return (
    <TextField
      label={label}
      value={value || ''}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      placeholder={TIME_INPUT_PLACEHOLDER}
      InputLabelProps={{ shrink: true }}
      onChange={(e) => onChange(sanitizeTimeTyping(e.target.value))}
      onBlur={(e) => {
        const normalized = normalizeTypedTime(e.target.value);
        if (normalized) onChange(normalized);
      }}
      inputProps={{
        inputMode: 'numeric',
        maxLength: 5,
        'aria-label': label,
        autoComplete: 'off'
      }}
    />
  );
}

const SPACE_PAGE_SX = {
  position: 'relative',
  minHeight: 'calc(100vh - 64px)',
  mx: { xs: -2, sm: -3 },
  mt: { xs: -2, sm: -3 },
  mb: { xs: -2, sm: -3 },
  px: { xs: 2, sm: 3 },
  pt: { xs: 2, sm: 3 },
  pb: { xs: 3, sm: 4 },
  bgcolor: 'transparent',
  color: TOKENS.ink
} as const;

const emptyForm = (date?: string): SessionFormState => ({
  sessionDate: date || format(new Date(), 'yyyy-MM-dd'),
  startTime: '11:00',
  endTime: '11:45',
  program: '',
  status: 'planned',
  audience: '',
  language: DEFAULT_LANGUAGE,
  clientContacts: '',
  comments: '',
  assigneeIds: [],
  allDay: false
});

function timeShort(t?: string | null) {
  if (!t) return '';
  return String(t).slice(0, 5);
}

function isAllDayTimes(start?: string | null, end?: string | null) {
  return timeShort(start) === ALL_DAY_START && timeShort(end) === ALL_DAY_END;
}

function statusLabel(status: SessionStatus) {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function colorForProgram(
  program?: string | null,
  catalog?: PlanetariumProgram[] | null
): string {
  const key = (program || '').trim();
  if (!key) return '#90caf9';
  if (catalog && catalog.length > 0) {
    const found = catalog.find(
      (p) => p.name.trim().toLowerCase() === key.toLowerCase() && p.color
    );
    if (found?.color && /^#[0-9A-Fa-f]{6}$/.test(found.color)) {
      return found.color.toLowerCase();
    }
  }
  return PROGRAM_COLOR_PALETTE[hashString(key.toLowerCase()) % PROGRAM_COLOR_PALETTE.length];
}

/** Soft program wash for dark glass session tiles */
function sessionTileBg(
  session: { status: SessionStatus; program?: string | null },
  catalog?: PlanetariumProgram[] | null
): string {
  if (STATUS_OVERRIDE_COLORS[session.status]) {
    return STATUS_OVERRIDE_COLORS[session.status] as string;
  }
  const hex = colorForProgram(session.program, catalog);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.62)`;
}

function formatUnderstaffedLabel(iso: string): string {
  try {
    return format(parseISO(iso), 'yyyy-MM-dd (EEEEEE)', { locale: lt });
  } catch {
    return iso;
  }
}

function assigneesLabel(s: PlanetariumSession): string | undefined {
  if (!s.assignedColleagues || s.assignedColleagues.length === 0) return undefined;
  return s.assignedColleagues.map((c) => `${c.firstName} ${c.lastName}`).join(', ');
}

function isAssignableSessionStatus(status: SessionStatus): boolean {
  return REQUESTABLE_SESSION_STATUSES.includes(status);
}

function isRequestableSessionStatus(status: SessionStatus): boolean {
  return REQUESTABLE_SESSION_STATUSES.includes(status);
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token || ''}`,
    'Content-Type': 'application/json'
  };
}

const Planetariumas: React.FC = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const canManage = hasPermission('planetarium.manage');
  const canAssign = hasPermission('planetarium.assign') || canManage;
  const canView = hasPermission('planetarium.view') || canAssign;
  const canRequestPrivate = canView && !canAssign;
  const canSeeLogs = hasPermission('planetarium.logs');
  const [activeSection, setActiveSection] = useState<PlanetariumSection>('calendar');

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [sessions, setSessions] = useState<PlanetariumSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<
    'all' | SessionStatus | 'unassigned' | 'private'
  >('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [programFilterInitialized, setProgramFilterInitialized] = useState(false);

  const [understaffedDates, setUnderstaffedDates] = useState<string[]>([]);
  const [staffPoolEmpty, setStaffPoolEmpty] = useState(false);
  const [assigneeConflicts, setAssigneeConflicts] = useState<AssigneeScheduleConflict[]>([]);
  const [assigneeConflictsExpanded, setAssigneeConflictsExpanded] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<SessionFormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [fromStaffPool, setFromStaffPool] = useState(true);

  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<Colleague[]>([]);
  const [staffPoolIds, setStaffPoolIds] = useState<string[]>([]);
  const [savingPool, setSavingPool] = useState(false);

  const [programs, setPrograms] = useState<PlanetariumProgram[]>([]);
  const [programsDialogOpen, setProgramsDialogOpen] = useState(false);
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramColor, setNewProgramColor] = useState('#90caf9');
  const [savingProgram, setSavingProgram] = useState(false);

  const [privateRequests, setPrivateRequests] = useState<PrivateRequest[]>([]);
  const [privateRequestsOpen, setPrivateRequestsOpen] = useState(false);
  const [reviewRequest, setReviewRequest] = useState<PrivateRequest | null>(null);
  const [requestContacts, setRequestContacts] = useState('');
  const [requestComments, setRequestComments] = useState('');
  const [requestedProgram, setRequestedProgram] = useState('');
  const [requestBaseline, setRequestBaseline] = useState<RequestBaseline | null>(null);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [reviewingRequestId, setReviewingRequestId] = useState<string | null>(null);
  const [newPrivateOpen, setNewPrivateOpen] = useState(false);
  const [newPrivateForm, setNewPrivateForm] = useState({
    sessionDate: '',
    startTime: '20:00',
    endTime: '21:00',
    program: '',
    clientContacts: '',
    comments: ''
  });

  const [assignMode, setAssignMode] = useState(false);
  const [assignWorker, setAssignWorker] = useState<Colleague | null>(null);
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [weeklyPlanOpen, setWeeklyPlanOpen] = useState(false);
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPlanState>(() => loadWeeklyPlan());
  const [populatingMonth, setPopulatingMonth] = useState(false);
  const [copiedDayPlan, setCopiedDayPlan] = useState<CopiedDaySession[]>(() => loadCopiedDayPlan());
  const [pastingDay, setPastingDay] = useState(false);
  const [clearDayDate, setClearDayDate] = useState<string | null>(null);

  const canEditRequestDraftFields = canManage || (canRequestPrivate && Boolean(form.id));
  const statusOptionsForForm = canManage
    ? STATUS_OPTIONS
    : STATUS_OPTIONS.filter(
        (o) => REQUESTABLE_SESSION_STATUSES.includes(o.value) || o.value === form.status
      );
  const baselineIsPlanned = requestBaseline?.status === 'planned';
  const baselineIsRequestable =
    Boolean(requestBaseline) && isRequestableSessionStatus(requestBaseline!.status);
  const hasSessionChangeDraft = (() => {
    if (!requestBaseline) return false;
    const nextProgram = requestedProgram.trim();
    const programChanged =
      Boolean(nextProgram) &&
      nextProgram.toLowerCase() !== requestBaseline.program.trim().toLowerCase();
    const statusChanged =
      isRequestableSessionStatus(form.status) && form.status !== requestBaseline.status;
    const languageChanged = form.language.trim() !== requestBaseline.language.trim();
    const audienceChanged = form.audience.trim() !== requestBaseline.audience.trim();
    return programChanged || statusChanged || languageChanged || audienceChanged;
  })();

  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    return eachDayOfInterval({
      start: startOfWeek(monthStart, { weekStartsOn: 1 }),
      end: endOfWeek(monthEnd, { weekStartsOn: 1 })
    });
  }, [currentMonth]);

  // Full calendar grid (includes adjacent-month days in leading/trailing weeks)
  const startDate = format(monthDays[0], 'yyyy-MM-dd');
  const endDate = format(monthDays[monthDays.length - 1], 'yyyy-MM-dd');

  const loadPrograms = useCallback(async () => {
    if (!canView) return;
    try {
      const res = await fetch(
        getApiUrl(`${API_ENDPOINTS.PLANETARIUM_PROGRAMS}?includeInactive=${canManage ? 'true' : 'false'}`),
        { headers: authHeaders() }
      );
      if (!res.ok) return;
      const data = await res.json();
      setPrograms(data.programs || []);
    } catch {
      /* ignore */
    }
  }, [canView, canManage]);

  const loadPrivateRequests = useCallback(async () => {
    if (!canView) return;
    try {
      const res = await fetch(
        getApiUrl(
          `${API_ENDPOINTS.PLANETARIUM_PRIVATE_REQUESTS}?status=pending&startDate=${startDate}&endDate=${endDate}`
        ),
        { headers: authHeaders() }
      );
      if (!res.ok) return;
      const data = await res.json();
      setPrivateRequests(data.requests || []);
    } catch {
      /* ignore */
    }
  }, [canView, startDate, endDate]);

  const loadMonth = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setError(null);
      const [sessRes, warnRes] = await Promise.all([
        fetch(
          getApiUrl(`${API_ENDPOINTS.PLANETARIUM_BY_DATE_RANGE}?startDate=${startDate}&endDate=${endDate}`),
          { headers: authHeaders() }
        ),
        fetch(
          getApiUrl(
            `${API_ENDPOINTS.PLANETARIUM_STAFFING_WARNINGS}?startDate=${startDate}&endDate=${endDate}`
          ),
          { headers: authHeaders() }
        )
      ]);

      if (!sessRes.ok) throw new Error('Nepavyko įkelti seansų');
      const sessData = await sessRes.json();
      setSessions(sessData.data || []);

      if (warnRes.ok) {
        const warnData = await warnRes.json();
        setUnderstaffedDates(warnData.understaffedDates || []);
        setStaffPoolEmpty(Boolean(warnData.staffPoolEmpty));
        setAssigneeConflicts(
          Array.isArray(warnData.assigneeConflicts) ? warnData.assigneeConflicts : []
        );
      }

      await loadPrivateRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setLoading(false);
    }
  }, [canView, startDate, endDate, loadPrivateRequests]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  const loadColleagues = useCallback(async () => {
    if (!canAssign) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_COLLEAGUES), {
        headers: authHeaders()
      });
      if (!res.ok) return;
      const data = await res.json();
      setColleagues(data.colleagues || []);
      setFromStaffPool(Boolean(data.fromStaffPool));
    } catch {
      /* ignore */
    }
  }, [canAssign]);

  useEffect(() => {
    loadColleagues();
  }, [loadColleagues]);

  const activeProgramNames = useMemo(
    () => programs.filter((p) => p.isActive).map((p) => p.name),
    [programs]
  );

  const languages = useMemo(() => {
    const set = new Set<string>();
    sessions.forEach((s) => {
      if (s.language) set.add(s.language);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'lt'));
  }, [sessions]);

  /** Program chips/options — independent of the program filter so chips stay visible while filtering. */
  const legendPrograms = useMemo(() => {
    const names = new Set<string>();
    activeProgramNames.forEach((n) => names.add(n));
    sessions.forEach((s) => {
      const name = (s.program || '').trim();
      if (name && s.status !== 'not_working' && s.status !== 'technical_day' && s.status !== 'planetarium_closed') {
        names.add(name);
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'lt'));
  }, [activeProgramNames, sessions]);

  useEffect(() => {
    if (legendPrograms.length === 0) return;
    if (!programFilterInitialized) {
      setSelectedPrograms(legendPrograms);
      setProgramFilterInitialized(true);
      return;
    }
    setSelectedPrograms((prev) => {
      const stillValid = prev.filter((p) => legendPrograms.includes(p));
      const newlyAdded = legendPrograms.filter((n) => !prev.includes(n));
      // If everything that existed before was selected, auto-select new programs too
      const oldPrograms = legendPrograms.filter((n) => !newlyAdded.includes(n));
      const oldAllSelected = oldPrograms.length > 0 && oldPrograms.every((n) => prev.includes(n));
      if (oldAllSelected && newlyAdded.length > 0) {
        return [...stillValid, ...newlyAdded];
      }
      return stillValid;
    });
  }, [legendPrograms, programFilterInitialized]);

  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (statusFilter === 'unassigned') {
        if (s.assignedColleagues && s.assignedColleagues.length > 0) return false;
      } else if (statusFilter === 'private') {
        if (s.status !== 'planned_private' && s.status !== 'confirmed_private') return false;
      } else if (statusFilter !== 'all' && s.status !== statusFilter) {
        return false;
      }
      if (languageFilter !== 'all' && (s.language || '') !== languageFilter) return false;

      if (s.status === 'not_working' || s.status === 'technical_day' || s.status === 'planetarium_closed') {
        return true;
      }
      if (legendPrograms.length === 0) return true;
      const name = (s.program || '').trim();
      if (!name) return selectedPrograms.length === legendPrograms.length;
      return selectedPrograms.includes(name);
    });
  }, [sessions, statusFilter, languageFilter, selectedPrograms, legendPrograms]);

  const unassignedFilteredSessionIds = useMemo(
    () =>
      filteredSessions
        .filter(
          (s) =>
            isAssignableSessionStatus(s.status) &&
            (!s.assignedColleagues || s.assignedColleagues.length === 0)
        )
        .map((s) => s.id),
    [filteredSessions]
  );

  const pendingRequestBySessionId = useMemo(() => {
    const map = new Map<string, PrivateRequest>();
    privateRequests.forEach((r) => {
      if (r.status !== 'pending') return;
      const sid = r.sessionId || r.session?.id;
      if (sid) map.set(sid, r);
    });
    return map;
  }, [privateRequests]);

  const pendingNewByDate = useMemo(() => {
    const map = new Map<string, PrivateRequest[]>();
    privateRequests.forEach((r) => {
      if (r.status !== 'pending' || r.requestType !== 'private_new') return;
      const key = String(r.sessionDate || '').slice(0, 10);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    for (const list of map.values()) {
      list.sort((a, b) => timeShort(a.startTime).localeCompare(timeShort(b.startTime)));
    }
    return map;
  }, [privateRequests]);

  const pendingRequestCount = privateRequests.filter((r) => r.status === 'pending').length;

  const toggleProgramFilter = (name: string) => {
    setSelectedPrograms((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]
    );
  };

  const selectAllPrograms = () => setSelectedPrograms(legendPrograms);

  const byDate = useMemo(() => {
    const map = new Map<string, PlanetariumSession[]>();
    filteredSessions.forEach((s) => {
      const key = String(s.sessionDate).slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    for (const list of map.values()) {
      list.sort((a, b) => timeShort(a.startTime).localeCompare(timeShort(b.startTime)));
    }
    return map;
  }, [filteredSessions]);

  const understaffedSet = useMemo(() => new Set(understaffedDates), [understaffedDates]);

  const conflictBySessionId = useMemo(() => {
    const map = new Map<string, AssigneeScheduleConflict>();
    assigneeConflicts.forEach((c) => map.set(c.sessionId, c));
    return map;
  }, [assigneeConflicts]);

  const conflictDatesSet = useMemo(() => {
    const set = new Set<string>();
    assigneeConflicts.forEach((c) => {
      if (c.sessionDate) set.add(c.sessionDate);
    });
    return set;
  }, [assigneeConflicts]);

  const openCreate = (date?: string) => {
    setForm(emptyForm(date));
    setRequestContacts('');
    setRequestComments('');
    setRequestedProgram('');
    setRequestBaseline(null);
    setDialogOpen(true);
  };

  const openEdit = (session: PlanetariumSession) => {
    const start = timeShort(session.startTime);
    const end = timeShort(session.endTime) || '';
    const allDay = isDayBlockStatus(session.status) && isAllDayTimes(start, end);
    const audience = session.audience || '';
    const language = session.language || DEFAULT_LANGUAGE;
    const program = session.program || '';
    setForm({
      id: session.id,
      sessionDate: String(session.sessionDate).slice(0, 10),
      startTime: start,
      endTime: end,
      program,
      status: session.status,
      audience,
      language,
      clientContacts: session.clientContacts || '',
      comments: session.comments || '',
      assigneeIds: (session.assignedColleagues || []).map((c) => c.id),
      allDay
    });
    setRequestBaseline({
      status: session.status,
      program,
      audience,
      language
    });
    setRequestContacts('');
    setRequestComments('');
    setRequestedProgram('');
    if (canAssign) void loadColleagues();
    setDialogOpen(true);
  };

  const saveSession = async () => {
    if (canManage) {
      setSaving(true);
      setError(null);
      try {
        const useAllDay = isDayBlockStatus(form.status) && form.allDay;
        const payload = {
          sessionDate: form.sessionDate,
          startTime: useAllDay ? ALL_DAY_START : form.startTime,
          endTime: useAllDay ? ALL_DAY_END : form.endTime || null,
          program: form.program,
          status: form.status,
          audience: form.audience || null,
          language: form.language || DEFAULT_LANGUAGE,
          clientContacts: form.clientContacts || null,
          comments: form.comments || null,
          assigneeIds: form.assigneeIds
        };

        const url = form.id
          ? getApiUrl(API_ENDPOINTS.PLANETARIUM_SESSION(form.id))
          : getApiUrl(API_ENDPOINTS.PLANETARIUM);
        const res = await fetch(url, {
          method: form.id ? 'PUT' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Nepavyko išsaugoti');
        setDialogOpen(false);
        setSuccess(form.id ? 'Seansas atnaujintas' : 'Seansas sukurtas');
        await loadMonth();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Klaida');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!canAssign || !form.id) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_SESSION(form.id)), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ assigneeIds: form.assigneeIds })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepavyko priskirti');
      setDialogOpen(false);
      setSuccess('Atsakingi atnaujinti');
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSaving(false);
    }
  };

  const deleteSession = async () => {
    if (!canManage || !form.id) return;
    if (!window.confirm('Ištrinti šį seansą?')) return;
    setSaving(true);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_SESSION(form.id)), {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Nepavyko ištrinti');
      setDialogOpen(false);
      setSuccess('Seansas ištrintas');
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSaving(false);
    }
  };

  const quickAddDay = async (date: string) => {
    if (!canManage) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_QUICK_DAY), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ sessionDate: date })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepavyko sukurti');
      setSuccess(
        data.createdCount
          ? `Pridėti ${data.createdCount} standartiniai seansai (${date})`
          : `Standartiniai seansai jau egzistuoja (${date})`
      );
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    }
  };

  const copyDayPlan = (date: string) => {
    if (!canManage) return;
    const daySessions = sessions
      .filter((s) => String(s.sessionDate).slice(0, 10) === date)
      .slice()
      .sort((a, b) => timeShort(a.startTime).localeCompare(timeShort(b.startTime)));
    if (daySessions.length === 0) {
      setError(t('planetarium.copyDayEmpty', 'Šią dieną nėra seansų kopijuoti'));
      setSuccess(null);
      return;
    }
    const payload = daySessions.map(sessionToCopiedPayload);
    setCopiedDayPlan(payload);
    persistCopiedDayPlan(payload);
    setError(null);
    setSuccess(
      t('planetarium.copyDaySuccess', 'Nukopijuota seansų: {{count}} ({{date}})', {
        count: payload.length,
        date
      })
    );
  };

  const replaceDaySessions = async (date: string, daySessions: CopiedDaySession[]) => {
    const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_REPLACE_DAY), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ sessionDate: date, sessions: daySessions })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || t('planetarium.pasteDayFailed', 'Nepavyko įklijuoti dienos plano'));
    }
    return data as { createdCount?: number };
  };

  const pasteDayPlan = async (date: string) => {
    if (!canManage || copiedDayPlan.length === 0 || pastingDay) return;
    setPastingDay(true);
    setError(null);
    try {
      const data = await replaceDaySessions(date, copiedDayPlan);
      setSuccess(
        t('planetarium.pasteDaySuccess', 'Įklijuota seansų: {{count}} į {{date}}', {
          count: data.createdCount ?? copiedDayPlan.length,
          date
        })
      );
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planetarium.pasteDayFailed', 'Nepavyko įklijuoti dienos plano'));
    } finally {
      setPastingDay(false);
    }
  };

  const clearDayPlan = async () => {
    if (!canManage || !clearDayDate || pastingDay) return;
    const date = clearDayDate;
    setPastingDay(true);
    setError(null);
    try {
      await replaceDaySessions(date, []);
      setSuccess(t('planetarium.clearDaySuccess', 'Ištrinti visos dienos seansai ({{date}})', { date }));
      setClearDayDate(null);
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planetarium.clearDayFailed', 'Nepavyko ištrinti dienos seansų'));
    } finally {
      setPastingDay(false);
    }
  };

  const openWeeklyPlan = () => {
    if (!canManage) return;
    setWeeklyPlan(loadWeeklyPlan());
    setWeeklyPlanOpen(true);
  };

  const setActiveWeeklyWeekday = (day: number) => {
    setWeeklyPlan((prev) => ({ ...prev, activeWeekday: day }));
  };

  const updateWeeklySlot = (id: string, patch: Partial<WeeklyPlanSlot>) => {
    setWeeklyPlan((prev) => {
      const key = String(prev.activeWeekday);
      const slots = (prev.byWeekday[key] || []).map((s) => (s.id === id ? { ...s, ...patch } : s));
      return { ...prev, byWeekday: { ...prev.byWeekday, [key]: slots } };
    });
  };

  const addWeeklySlot = () => {
    setWeeklyPlan((prev) => {
      const key = String(prev.activeWeekday);
      const slots = [...(prev.byWeekday[key] || []), defaultWeeklySlot('11:00')];
      return { ...prev, byWeekday: { ...prev.byWeekday, [key]: slots } };
    });
  };

  const addWeeklyTechnicalDay = () => {
    setWeeklyPlan((prev) => {
      const key = String(prev.activeWeekday);
      const existing = prev.byWeekday[key] || [];
      if (existing.some((s) => s.status === 'technical_day')) return prev;
      return {
        ...prev,
        byWeekday: { ...prev.byWeekday, [key]: [...existing, defaultTechnicalDaySlot()] }
      };
    });
  };

  const addWeeklyPlanetariumClosed = () => {
    setWeeklyPlan((prev) => {
      const key = String(prev.activeWeekday);
      const existing = prev.byWeekday[key] || [];
      if (existing.some((s) => s.status === 'planetarium_closed')) return prev;
      return {
        ...prev,
        byWeekday: { ...prev.byWeekday, [key]: [...existing, defaultPlanetariumClosedSlot()] }
      };
    });
  };

  const removeWeeklySlot = (id: string) => {
    setWeeklyPlan((prev) => {
      const key = String(prev.activeWeekday);
      const slots = (prev.byWeekday[key] || []).filter((s) => s.id !== id);
      return { ...prev, byWeekday: { ...prev.byWeekday, [key]: slots } };
    });
  };

  const populateMonthFromWeeklyPlan = async () => {
    if (!canManage) return;
    if (!weeklyPlanHasSlots(weeklyPlan)) return;
    saveWeeklyPlan(weeklyPlan);
    setPopulatingMonth(true);
    try {
      const byWeekday: Record<
        string,
        Array<{
          startTime: string;
          durationMinutes: number;
          program: string;
          language: string;
          status: WeeklyPlanSlotStatus;
        }>
      > = {};
      for (const [day, slots] of Object.entries(weeklyPlan.byWeekday)) {
        const clean = slots
          .filter(
            (s) =>
              s.status === 'technical_day' ||
              s.status === 'planetarium_closed' ||
              Boolean(s.startTime)
          )
          .map((s) =>
            s.status === 'technical_day' || s.status === 'planetarium_closed'
              ? {
                  startTime: ALL_DAY_START,
                  durationMinutes: 24 * 60 - 1,
                  program: s.program || '',
                  language: s.language || DEFAULT_LANGUAGE,
                  status: s.status
                }
              : {
                  startTime: s.startTime,
                  durationMinutes: s.durationMinutes,
                  program: s.program,
                  language: s.language || DEFAULT_LANGUAGE,
                  status: 'planned' as const
                }
          );
        if (clean.length > 0) byWeekday[day] = clean;
      }

      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_POPULATE_MONTH), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          year: currentMonth.getFullYear(),
          month: currentMonth.getMonth() + 1,
          byWeekday
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepavyko užpildyti mėnesio');
      const monthLabel = formatMonthYearLt(currentMonth);
      setSuccess(
        `Užpildyta ${monthLabel}: sukurta ${data.createdCount || 0}` +
          (data.skippedCount ? `, praleista ${data.skippedCount}` : '')
      );
      setWeeklyPlanOpen(false);
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setPopulatingMonth(false);
    }
  };

  const openStaffPool = async () => {
    if (!canManage) return;
    try {
      const [poolRes, usersRes] = await Promise.all([
        fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_STAFF_POOL), { headers: authHeaders() }),
        fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_USERS_FOR_POOL), { headers: authHeaders() })
      ]);
      if (poolRes.ok) {
        const poolData = await poolRes.json();
        setStaffPoolIds((poolData.members || []).map((m: Colleague) => m.id));
      }
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setAllUsers(usersData.users || []);
      }
      setStaffDialogOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    }
  };

  const saveStaffPool = async () => {
    setSavingPool(true);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_STAFF_POOL), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ userIds: staffPoolIds })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepavyko išsaugoti');
      setStaffDialogOpen(false);
      setSuccess('Planetariumo darbuotojų grupė atnaujinta');
      await loadColleagues();
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSavingPool(false);
    }
  };

  const addProgram = async () => {
    const name = newProgramName.trim();
    if (!name) return;
    setSavingProgram(true);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PROGRAMS), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name, color: newProgramColor })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepavyko pridėti programos');
      setNewProgramName('');
      setNewProgramColor('#90caf9');
      await loadPrograms();
      setSuccess('Programa pridėta');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSavingProgram(false);
    }
  };

  const updateProgramColor = async (id: string, color: string) => {
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PROGRAM(id)), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ color })
      });
      if (!res.ok) throw new Error('Nepavyko išsaugoti spalvos');
      setPrograms((prev) => prev.map((p) => (p.id === id ? { ...p, color } : p)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    }
  };

  const deactivateProgram = async (id: string) => {
    if (!window.confirm('Išjungti šią programą iš sąrašo?')) return;
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PROGRAM(id)), {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Nepavyko išjungti');
      await loadPrograms();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    }
  };

  const reactivateProgram = async (id: string) => {
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PROGRAM(id)), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ isActive: true })
      });
      if (!res.ok) throw new Error('Nepavyko atkurti');
      await loadPrograms();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    }
  };

  const submitPrivateRequest = async () => {
    if (!canRequestPrivate || !form.id || requestBaseline?.status !== 'planned') return;
    if (!requestContacts.trim() && !requestComments.trim()) {
      setError('Įrašykite kontaktus arba komentarą prašymui');
      return;
    }
    const requestedStatus =
      form.status === 'planned_private' || form.status === 'confirmed_private'
        ? form.status
        : 'confirmed_private';
    const payload: Record<string, string | null> = {
      clientContacts: requestContacts.trim() || null,
      comments: requestComments.trim() || null,
      requestedStatus
    };
    if (requestBaseline) {
      const lang = form.language.trim();
      if (lang !== requestBaseline.language.trim()) {
        payload.requestedLanguage = lang || null;
      }
      const audience = form.audience.trim();
      if (audience !== requestBaseline.audience.trim()) {
        payload.requestedAudience = audience || null;
      }
    }
    setSubmittingRequest(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PRIVATE_REQUEST(form.id)), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Nepavyko pateikti prašymo');
      setSuccess('Privataus seanso prašymas pateiktas');
      setDialogOpen(false);
      setRequestContacts('');
      setRequestComments('');
      setRequestBaseline(null);
      await loadPrivateRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const submitProgramChangeRequest = async () => {
    if (!canRequestPrivate || !form.id || !requestBaseline) return;
    if (!isRequestableSessionStatus(requestBaseline.status)) return;

    const nextProgram = requestedProgram.trim();
    const programChanged =
      Boolean(nextProgram) &&
      nextProgram.toLowerCase() !== requestBaseline.program.trim().toLowerCase();
    const statusChanged =
      isRequestableSessionStatus(form.status) && form.status !== requestBaseline.status;
    const languageChanged = form.language.trim() !== requestBaseline.language.trim();
    const audienceChanged = form.audience.trim() !== requestBaseline.audience.trim();

    if (!programChanged && !statusChanged && !languageChanged && !audienceChanged) {
      setError('Pasirinkite programos, statuso, kalbos ar „Kam skirta“ pakeitimą');
      return;
    }

    const payload: Record<string, string | null> = {
      comments: requestComments.trim() || null
    };
    if (programChanged) payload.requestedProgram = nextProgram;
    if (statusChanged) payload.requestedStatus = form.status;
    if (languageChanged) payload.requestedLanguage = form.language.trim() || null;
    if (audienceChanged) payload.requestedAudience = form.audience.trim() || null;

    setSubmittingRequest(true);
    setError(null);
    try {
      const res = await fetch(
        getApiUrl(API_ENDPOINTS.PLANETARIUM_PROGRAM_CHANGE_REQUEST(form.id)),
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Nepavyko pateikti prašymo');
      setSuccess('Seanso keitimo prašymas pateiktas');
      setDialogOpen(false);
      setRequestedProgram('');
      setRequestComments('');
      setRequestBaseline(null);
      await loadPrivateRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const openNewPrivateRequest = (dateKey: string) => {
    if (!canRequestPrivate) return;
    setNewPrivateForm({
      sessionDate: dateKey,
      startTime: '20:00',
      endTime: '21:00',
      program: '',
      clientContacts: '',
      comments: ''
    });
    setNewPrivateOpen(true);
  };

  const submitNewPrivateRequest = async () => {
    if (!canRequestPrivate) return;
    if (!newPrivateForm.sessionDate || !newPrivateForm.startTime) {
      setError('Data ir pradžios laikas privalomi');
      return;
    }
    if (!newPrivateForm.clientContacts.trim() && !newPrivateForm.comments.trim()) {
      setError('Įrašykite kontaktus arba komentarą prašymui');
      return;
    }
    setSubmittingRequest(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PRIVATE_REQUESTS), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          sessionDate: newPrivateForm.sessionDate,
          startTime: newPrivateForm.startTime,
          endTime: newPrivateForm.endTime || null,
          program: newPrivateForm.program.trim(),
          clientContacts: newPrivateForm.clientContacts.trim() || null,
          comments: newPrivateForm.comments.trim() || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Nepavyko pateikti prašymo');
      setSuccess('Naujo privataus seanso prašymas pateiktas');
      setNewPrivateOpen(false);
      await loadPrivateRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const openRequestReview = (req: PrivateRequest) => {
    if (!canManage) return;
    setReviewRequest(req);
  };

  const closeRequestReview = () => {
    setReviewRequest(null);
  };

  const acceptPrivateRequest = async (id: string) => {
    setReviewingRequestId(id);
    setError(null);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PRIVATE_REQUEST_ACCEPT(id)), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Nepavyko patvirtinti');
      setSuccess('Prašymas patvirtintas');
      setReviewRequest(null);
      setPrivateRequestsOpen(false);
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setReviewingRequestId(null);
    }
  };

  const denyPrivateRequest = async (id: string) => {
    const note = window.prompt('Atmetimo priežastis (neprivaloma):') ?? undefined;
    if (note === undefined) return; // cancelled prompt
    setReviewingRequestId(id);
    setError(null);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_PRIVATE_REQUEST_DENY(id)), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reviewNote: note.trim() || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Nepavyko atmesti');
      setSuccess('Prašymas atmestas');
      setReviewRequest(null);
      await loadPrivateRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setReviewingRequestId(null);
    }
  };

  const selectedAssignees = useMemo(
    () => colleagues.filter((c) => form.assigneeIds.includes(c.id)),
    [colleagues, form.assigneeIds]
  );

  const staffSelected = useMemo(
    () => allUsers.filter((u) => staffPoolIds.includes(u.id)),
    [allUsers, staffPoolIds]
  );

  const enterAssignMode = () => {
    if (!canAssign) return;
    setAssignMode(true);
    setAssignWorker(null);
    setSelectedSessionIds([]);
    setOnlyUnassigned(false);
    void loadColleagues();
  };

  const exitAssignMode = () => {
    setAssignMode(false);
    setAssignWorker(null);
    setSelectedSessionIds([]);
    setOnlyUnassigned(false);
    setAssigning(false);
  };

  const toggleSessionSelect = (session: PlanetariumSession) => {
    if (!isAssignableSessionStatus(session.status)) return;
    const hasAssignees = Boolean(session.assignedColleagues && session.assignedColleagues.length > 0);
    if (onlyUnassigned && hasAssignees) return;
    setSelectedSessionIds((prev) =>
      prev.includes(session.id) ? prev.filter((id) => id !== session.id) : [...prev, session.id]
    );
  };

  const assignSessions = async (
    sessionIds: string[],
    options?: { clearWorker?: boolean }
  ) => {
    if (!canAssign || !assignWorker || sessionIds.length === 0) return;
    setAssigning(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.PLANETARIUM_ASSIGN_BULK), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          userId: assignWorker.id,
          sessionIds
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Nepavyko priskirti');
      const count = typeof data.updatedCount === 'number' ? data.updatedCount : sessionIds.length;
      setSuccess(
        count > 0
          ? `Priskirta: ${assignWorker.firstName} ${assignWorker.lastName} → ${count} seans.`
          : 'Pasirinkti seansai jau turėjo šį darbuotoją'
      );
      setSelectedSessionIds([]);
      if (options?.clearWorker !== false) {
        setAssignWorker(null);
      }
      setOnlyUnassigned(true);
      await loadMonth();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Klaida');
    } finally {
      setAssigning(false);
    }
  };

  const confirmAssign = async () => {
    await assignSessions(selectedSessionIds, { clearWorker: true });
  };

  const assignAllUnassigned = async () => {
    await assignSessions(unassignedFilteredSessionIds, { clearWorker: false });
  };

  if (!canView) {
    return (
      <Box sx={{ ...SPACE_PAGE_SX, display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ position: 'relative', zIndex: 1, p: 3, width: '100%', maxWidth: 1400, mx: 'auto' }}>
          <Alert
            severity="warning"
            sx={{
              ...ALERT_BASE_SX,
              bgcolor: TOKENS.warnFill,
              border: `1px solid ${TOKENS.warnBorder}`,
              color: TOKENS.warnInk,
              '& .MuiAlert-icon': { color: TOKENS.warnInk }
            }}
          >
            Neturite teisių peržiūrėti planetariumo seansų.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={SPACE_PAGE_SX}>
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1400,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              mb: 0.75,
              color: TOKENS.ink,
              fontWeight: 700,
              letterSpacing: '0.03em',
              fontSize: { xs: '1.7rem', md: '1.95rem' }
            }}
          >
            {t('navigation.planetariumas', 'Planetariumas')}
          </Typography>
          <Box
            sx={{
              width: 56,
              height: 2,
              mb: 1,
              borderRadius: 1,
              background: `linear-gradient(90deg, ${TOKENS.accent}, transparent)`
            }}
          />
          <Typography variant="body2" sx={{ color: TOKENS.muted, maxWidth: 520 }}>
            Seansų kalendorius, darbuotojų priskyrimas ir grafiko įspėjimai
          </Typography>
        </Box>

        {canSeeLogs && (
          <Box sx={{ borderBottom: 1, borderColor: TOKENS.line }}>
            <Tabs
              value={activeSection}
              onChange={(_, value: PlanetariumSection) => setActiveSection(value)}
              textColor="inherit"
              TabIndicatorProps={{ sx: { bgcolor: TOKENS.accent } }}
              sx={{
                minHeight: 42,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  color: TOKENS.muted,
                  minHeight: 42
                },
                '& .Mui-selected': { color: `${TOKENS.ink} !important` }
              }}
            >
              <Tab value="calendar" label="Kalendorius" />
              <Tab value="logs" label="Veiklos žurnalas" />
            </Tabs>
          </Box>
        )}

        {activeSection === 'logs' && canSeeLogs ? (
          <Paper elevation={0} sx={{ ...GLASS_SX, p: { xs: 1.5, md: 2 } }}>
            <PlanetariumActivityLog embedded />
          </Paper>
        ) : (
          <>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              ...ALERT_BASE_SX,
              bgcolor: TOKENS.dangerFill,
              border: `1px solid ${TOKENS.dangerBorder}`,
              '& .MuiAlert-icon': { color: '#c03939' }
            }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{
              ...ALERT_BASE_SX,
              bgcolor: TOKENS.okFill,
              border: `1px solid ${TOKENS.okBorder}`,
              '& .MuiAlert-icon': { color: '#2e7d32' }
            }}
          >
            {success}
          </Alert>
        )}

        {(understaffedDates.length > 0 || staffPoolEmpty) && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{
              ...ALERT_BASE_SX,
              bgcolor: TOKENS.warnFill,
              border: `1px solid ${TOKENS.warnBorder}`,
              color: TOKENS.warnInk,
              '& .MuiAlert-icon': { color: TOKENS.warnInk }
            }}
          >
            <Typography sx={{ fontWeight: 700, color: TOKENS.warnInk, mb: 0.75, fontSize: '0.9rem' }}>
              Personalo įspėjimas
            </Typography>
            {staffPoolEmpty && (
              <Typography sx={{ color: TOKENS.warnInk, mb: understaffedDates.length ? 1 : 0, fontSize: '0.875rem' }}>
                Planetariumo darbuotojų grupė tuščia — nustatykite personalą (mygtukas „Darbuotojai“).
              </Typography>
            )}
            {understaffedDates.length > 0 && (
              <Box>
                <Typography sx={{ color: TOKENS.warnInk, mb: 1, fontSize: '0.875rem' }}>
                  Dienos su seansais, bet grafike nėra planetariumo grupės nario su statusu „Dirba“
                  (nepriklauso nuo to, ar seansams jau priskirtas atsakingas asmuo):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {understaffedDates.map((d) => (
                    <Chip
                      key={d}
                      size="small"
                      label={formatUnderstaffedLabel(d)}
                      sx={{
                        height: 26,
                        fontWeight: 600,
                        color: TOKENS.warnInk,
                        bgcolor: 'rgba(0, 0, 0, 0.22)',
                        border: `1px solid ${TOKENS.warnBorder}`
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Alert>
        )}

        {assigneeConflicts.length > 0 && (
          <Alert
            severity="error"
            icon={<AssigneeConflictIcon />}
            sx={{
              ...ALERT_BASE_SX,
              bgcolor: TOKENS.conflictFill,
              border: `2px solid ${TOKENS.conflictBorder}`,
              color: TOKENS.conflictInk,
              '& .MuiAlert-icon': { color: TOKENS.conflictInk },
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setAssigneeConflictsExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setAssigneeConflictsExpanded((v) => !v);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                cursor: 'pointer',
                userSelect: 'none',
                mb: assigneeConflictsExpanded ? 0.75 : 0
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 800, color: TOKENS.conflictInk, fontSize: '0.95rem', lineHeight: 1.25 }}
                >
                  PRISKIRIMO KONFLIKTAS SU DARBO GRAFIKU
                </Typography>
                <Typography sx={{ color: TOKENS.conflictInk, fontSize: '0.8rem', opacity: 0.9 }}>
                  {assigneeConflicts.length}{' '}
                  {assigneeConflicts.length === 1 ? 'seansas' : 'seansai'} · spauskite, kad{' '}
                  {assigneeConflictsExpanded ? 'suskleistumėte' : 'išskleistumėte'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={assigneeConflictsExpanded ? 'Suskleisti' : 'Išskleisti'}
                sx={{ color: TOKENS.conflictInk, flexShrink: 0 }}
              >
                {assigneeConflictsExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            <Collapse in={assigneeConflictsExpanded}>
              <Typography sx={{ color: TOKENS.conflictInk, mb: 1, fontSize: '0.875rem' }}>
                Priskirtas darbuotojas tą dieną nėra su statusu „Dirba“ Darbo grafike. Magentos rėmeliai
                kalendoriuje žymi konfliktinius seansus.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
                {assigneeConflicts.map((c) => (
                  <Box
                    key={c.sessionId}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 0.6,
                      py: 0.35,
                      px: 0.75,
                      borderRadius: 1,
                      bgcolor: 'rgba(176, 0, 120, 0.08)',
                      border: `1px solid ${TOKENS.conflictBorder}`
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 700, fontSize: '0.8rem', color: TOKENS.conflictInk }}
                    >
                      {formatUnderstaffedLabel(c.sessionDate)}
                      {c.startTime ? ` · ${c.startTime}` : ''}
                      {c.program ? ` · ${c.program}` : ''}
                    </Typography>
                    {c.assignees.map((a) => (
                      <Chip
                        key={`${c.sessionId}-${a.userId}`}
                        size="small"
                        label={`${a.firstName} ${a.lastName}: ${scheduleStatusLabel(
                          a.scheduleStatus,
                          a.scheduleApproved
                        )}`}
                        sx={{
                          height: 24,
                          fontWeight: 700,
                          color: '#fff',
                          bgcolor: TOKENS.conflictInk,
                          '& .MuiChip-label': { px: 0.85 }
                        }}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Alert>
        )}

        <Paper elevation={0} sx={{ ...GLASS_SX, p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            sx={{ flexWrap: 'wrap', alignItems: { lg: 'center' } }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel sx={{ color: TOKENS.muted }}>Statusas</InputLabel>
                <Select
                  label="Statusas"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  sx={FIELD_SX}
                  MenuProps={{
                    PaperProps: {
                      sx: MENU_PAPER_SX
                    }
                  }}
                >
                  <MenuItem value="all">Visi</MenuItem>
                  <MenuItem value="private">Privatūs (visi)</MenuItem>
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="unassigned">Be atsakingo</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: TOKENS.muted }}>Kalba</InputLabel>
                <Select
                  label="Kalba"
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  sx={FIELD_SX}
                  MenuProps={{
                    PaperProps: {
                      sx: MENU_PAPER_SX
                    }
                  }}
                >
                  <MenuItem value="all">Visos</MenuItem>
                  {languages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {(canManage || canAssign) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, ml: { lg: 'auto' } }}>
                <Button
                  variant={assignMode ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => (assignMode ? exitAssignMode() : enterAssignMode())}
                  sx={assignMode ? { ...BTN_PRIMARY_SX, px: 1.5 } : BTN_GHOST_SX}
                >
                  Priskyrimo režimas
                </Button>
                {canManage && (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openCreate()}
                      disabled={assignMode}
                      sx={{ ...BTN_PRIMARY_SX, px: 1.5 }}
                    >
                      Naujas seansas
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InboxIcon />}
                      onClick={() => {
                        void loadPrivateRequests();
                        setPrivateRequestsOpen(true);
                      }}
                      sx={BTN_GHOST_SX}
                    >
                      Prašymai{pendingRequestCount > 0 ? ` (${pendingRequestCount})` : ''}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<WeekPlanIcon />}
                      onClick={openWeeklyPlan}
                      sx={BTN_GHOST_SX}
                    >
                      Savaitės planas
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ProgramsIcon />}
                      onClick={() => setProgramsDialogOpen(true)}
                      sx={BTN_GHOST_SX}
                    >
                      Programos
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GroupIcon />}
                      onClick={openStaffPool}
                      sx={BTN_GHOST_SX}
                    >
                      Darbuotojai
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Stack>

          <Stack
            spacing={1}
            alignItems="center"
            sx={{
              mt: 1.75,
              pt: 1.5,
              borderTop: `1px solid ${TOKENS.line}`
            }}
          >
            <Typography sx={{ fontWeight: 700, color: TOKENS.ink, fontSize: '1.15rem', textAlign: 'center' }}>
              {formatMonthYearLt(currentMonth)}
            </Typography>
            <ButtonGroup
              size="small"
              variant="outlined"
              sx={{
                '& .MuiButton-root': BTN_GHOST_SX
              }}
            >
              <Button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>Atgal</Button>
              <Button onClick={() => setCurrentMonth(new Date())}>Šiandien</Button>
              <Button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>Pirmyn</Button>
            </ButtonGroup>
            <Typography sx={{ color: TOKENS.muted, fontSize: '0.875rem', fontWeight: 500 }}>
              {loading ? 'Kraunama…' : `${filteredSessions.length} seansai`}
            </Typography>
          </Stack>

          {assignMode && canAssign && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                border: `1px solid ${TOKENS.accent}`,
                bgcolor: 'rgba(61, 184, 197, 0.12)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1.25,
                alignItems: { md: 'center' }
              }}
            >
              <Autocomplete
                size="small"
                options={colleagues}
                value={assignWorker}
                onChange={(_, value) => setAssignWorker(value)}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                sx={{ minWidth: { xs: '100%', md: 240 }, flex: { md: '0 1 280px' } }}
                slotProps={{ paper: { sx: MENU_PAPER_SX } }}
                renderInput={(params) => (
                  <TextField {...params} label="Darbuotojas" placeholder="Pasirinkite" />
                )}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={onlyUnassigned}
                    onChange={(e) => {
                      setOnlyUnassigned(e.target.checked);
                      setSelectedSessionIds([]);
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: TOKENS.ink, fontWeight: 600 }}>
                    Tik nepriskirti
                  </Typography>
                }
              />
              <Typography variant="body2" sx={{ color: TOKENS.muted, fontWeight: 600 }}>
                Pasirinkta: {selectedSessionIds.length}
                {unassignedFilteredSessionIds.length > 0
                  ? ` · Nepriskirti: ${unassignedFilteredSessionIds.length}`
                  : ''}
              </Typography>
              <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!assignWorker || unassignedFilteredSessionIds.length === 0 || assigning}
                  onClick={() => void assignAllUnassigned()}
                  sx={{ ...BTN_GHOST_SX, flex: { xs: 1, md: 'none' } }}
                >
                  {assigning ? 'Priskiriama…' : 'Priskirti visus nepriskirtus'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!assignWorker || selectedSessionIds.length === 0 || assigning}
                  onClick={() => void confirmAssign()}
                  sx={{ ...BTN_PRIMARY_SX, flex: { xs: 1, md: 'none' } }}
                >
                  {assigning ? 'Priskiriama…' : 'Patvirtinti'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={exitAssignMode}
                  disabled={assigning}
                  sx={{ ...BTN_GHOST_SX, flex: { xs: 1, md: 'none' } }}
                >
                  Išeiti
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>

        <Paper elevation={0} sx={{ ...GLASS_SX, p: { xs: 1.25, md: 1.5 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  mr: 0.5,
                  color: TOKENS.muted,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Programos
              </Typography>
              {legendPrograms.length > 0 && (
                  <Chip
                    size="small"
                    label="Visos programos"
                    clickable
                    onClick={selectAllPrograms}
                    variant={selectedPrograms.length === legendPrograms.length ? 'filled' : 'outlined'}
                    sx={{
                      ...CHIP_BASE_SX,
                      ...(selectedPrograms.length === legendPrograms.length
                        ? {
                            bgcolor: TOKENS.accent,
                            color: '#041820',
                            borderColor: TOKENS.accent,
                            '&:hover': { bgcolor: TOKENS.accentHover }
                          }
                        : {})
                    }}
                  />
              )}
              <Chip
                size="small"
                label="Privatūs"
                clickable
                onClick={() =>
                  setStatusFilter((prev) => (prev === 'private' ? 'all' : 'private'))
                }
                variant={statusFilter === 'private' ? 'filled' : 'outlined'}
                sx={{
                  ...CHIP_BASE_SX,
                  ...(statusFilter === 'private'
                    ? {
                        bgcolor: 'rgba(156, 120, 255, 0.75)',
                        color: '#041820',
                        borderColor: 'rgba(156, 120, 255, 0.95)',
                        '&:hover': { bgcolor: 'rgba(156, 120, 255, 0.85)' }
                      }
                    : {
                        borderColor: 'rgba(156, 120, 255, 0.65)',
                        color: TOKENS.ink
                      })
                }}
              />
              {legendPrograms.map((name) => {
                const selected = selectedPrograms.includes(name);
                const tone = colorForProgram(name, programs);
                const r = parseInt(tone.slice(1, 3), 16);
                const g = parseInt(tone.slice(3, 5), 16);
                const b = parseInt(tone.slice(5, 7), 16);
                return (
                  <Chip
                    key={name}
                    size="small"
                    label={name}
                    clickable
                    onClick={() => toggleProgramFilter(name)}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      ...CHIP_BASE_SX,
                      bgcolor: selected ? `rgba(${r}, ${g}, ${b}, 0.65)` : 'transparent',
                      borderColor: selected ? `rgba(${r}, ${g}, ${b}, 0.85)` : tone,
                      opacity: selected ? 1 : 0.75,
                      color: TOKENS.ink
                    }}
                  />
                );
              })}
            </Box>
          </Paper>

        <Paper elevation={0} sx={{ ...GLASS_CALENDAR_SX, overflow: 'hidden', p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map((d) => (
              <Box
                key={d}
                sx={{
                  py: 1,
                  px: 0.5,
                  borderBottom: `1px solid ${TOKENS.line}`,
                  fontWeight: 700,
                  textAlign: 'center',
                  bgcolor: 'rgba(17, 17, 17, 0.04)',
                  color: TOKENS.muted,
                  fontSize: '0.78rem',
                  letterSpacing: '0.06em'
                }}
              >
                {d}
              </Box>
            ))}
            {monthDays.map((date) => {
              const key = format(date, 'yyyy-MM-dd');
              const daySessions = byDate.get(key) || [];
              const isUnderstaffed = understaffedSet.has(key);
              const hasAssigneeConflict = conflictDatesSet.has(key);
              const inMonth = isSameMonth(date, currentMonth);
              const hasFullDayTechnical = daySessions.some(isFullDayTechnicalSession);
              const dayCell = (
                <Box
                  sx={{
                    minHeight: hasFullDayTechnical ? 168 : 128,
                    height: '100%',
                    borderBottom: `1px solid ${TOKENS.line}`,
                    borderRight: `1px solid ${TOKENS.line}`,
                    p: 0.85,
                    opacity: inMonth ? 1 : 0.4,
                    bgcolor: isUnderstaffed
                      ? TOKENS.dangerFill
                      : hasAssigneeConflict
                        ? TOKENS.conflictFill
                        : 'transparent',
                    boxShadow: isUnderstaffed
                      ? 'inset 0 0 0 2px rgba(192, 57, 57, 0.85)'
                      : hasAssigneeConflict
                        ? `inset 0 0 0 2px ${TOKENS.conflictBorder}`
                        : 'none'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.65,
                      gap: 0.5,
                      alignItems: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        minWidth: 26,
                        height: 24,
                        px: 0.65,
                        borderRadius: 1,
                        bgcolor: 'rgba(17, 17, 17, 0.06)',
                        border: isUnderstaffed
                          ? '1px solid rgba(192, 57, 57, 0.55)'
                          : hasAssigneeConflict
                            ? `1px solid ${TOKENS.conflictBorder}`
                            : `1px solid ${TOKENS.line}`
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: TOKENS.ink, lineHeight: 1, fontSize: '0.78rem' }}
                      >
                        {format(date, 'd')}
                      </Typography>
                      {isUnderstaffed && (
                        <WarningIcon sx={{ fontSize: 13, color: '#c03939' }} />
                      )}
                      {!isUnderstaffed && hasAssigneeConflict && (
                        <AssigneeConflictIcon sx={{ fontSize: 13, color: TOKENS.conflictInk }} />
                      )}
                    </Box>
                    {canManage && (
                      <Box sx={{ display: 'flex', gap: 0.35, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Tooltip title={t('planetarium.copyDayTooltip', 'Kopijuoti dienos planą')}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disableElevation
                              onClick={() => copyDayPlan(key)}
                              sx={{
                                ...DAY_ACTION_SX,
                                ...BTN_GHOST_SX,
                                minWidth: 26,
                                px: 0.35
                              }}
                            >
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('planetarium.pasteDayTooltip', 'Įklijuoti dienos planą')}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disableElevation
                              disabled={copiedDayPlan.length === 0 || pastingDay}
                              onClick={() => pasteDayPlan(key)}
                              sx={{
                                ...DAY_ACTION_SX,
                                ...BTN_GHOST_SX,
                                minWidth: 26,
                                px: 0.35,
                                opacity: copiedDayPlan.length === 0 ? 0.4 : 1
                              }}
                            >
                              <PasteIcon sx={{ fontSize: 14 }} />
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('planetarium.clearDayTooltip', 'Ištrinti visus dienos seansus')}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disableElevation
                              disabled={daySessions.length === 0 || pastingDay}
                              onClick={() => setClearDayDate(key)}
                              sx={{
                                ...DAY_ACTION_SX,
                                minWidth: 26,
                                px: 0.35,
                                color: '#c03939',
                                borderColor: TOKENS.dangerBorder,
                                bgcolor: TOKENS.dangerFill,
                                opacity: daySessions.length === 0 ? 0.4 : 1,
                                '&:hover': {
                                  bgcolor: 'rgba(255, 90, 90, 0.32)',
                                  borderColor: '#ff7a7a'
                                }
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 16, fontWeight: 800 }} />
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Standartinė diena 11/13/15/17">
                          <Button
                            size="small"
                            variant="outlined"
                            disableElevation
                            onClick={() => quickAddDay(key)}
                            sx={{
                              ...DAY_ACTION_SX,
                              ...BTN_GHOST_SX
                            }}
                          >
                            4×
                          </Button>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={() => openCreate(key)}
                          sx={{
                            ...DAY_ACTION_SX,
                            ...BTN_PRIMARY_SX
                          }}
                        >
                          +
                        </Button>
                      </Box>
                    )}
                    {canRequestPrivate && (
                      <Tooltip title="Prašyti naujo privataus seanso">
                        <Button
                          size="small"
                          variant="outlined"
                          disableElevation
                          onClick={() => openNewPrivateRequest(key)}
                          sx={{
                            ...DAY_ACTION_SX,
                            ...BTN_GHOST_SX,
                            borderColor: 'rgba(255, 196, 77, 0.55)',
                            color: TOKENS.ink
                          }}
                        >
                          +Priv
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
                    {(pendingNewByDate.get(key) || []).map((req) => (
                      <Button
                        key={req.id}
                        size="small"
                        onClick={() => {
                          if (canManage) openRequestReview(req);
                        }}
                        disabled={!canManage}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          lineHeight: 1.2,
                          bgcolor: 'rgba(255, 196, 77, 0.22)',
                          color: TOKENS.ink,
                          ...PENDING_REQUEST_CHIP_SX,
                          borderRadius: 1,
                          px: 0.7,
                          py: 0.45,
                          minWidth: 0,
                          opacity: canManage ? 1 : 0.85,
                          cursor: canManage ? 'pointer' : 'default',
                          '&:hover': {
                            bgcolor: 'rgba(255, 196, 77, 0.3)',
                            borderColor: 'rgba(255, 196, 77, 1)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.45,
                            width: '100%',
                            minWidth: 0
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              color: TOKENS.ink,
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'left'
                            }}
                          >
                            {timeShort(req.startTime)} {req.program || 'Privatus'}
                          </Typography>
                          <Box
                            component="span"
                            sx={{
                              flexShrink: 0,
                              px: 0.45,
                              py: 0.1,
                              borderRadius: 0.75,
                              fontSize: '0.58rem',
                              fontWeight: 800,
                              letterSpacing: '0.03em',
                              lineHeight: 1.2,
                              color: '#041820',
                              bgcolor: 'rgba(255, 196, 77, 0.95)'
                            }}
                          >
                            Prašymas
                          </Box>
                        </Box>
                      </Button>
                    ))}
                    {daySessions.slice(0, 5).map((session) => {
                      const tallTechnical = isFullDayTechnicalSession(session);
                      const pendingReq = pendingRequestBySessionId.get(session.id);
                      const hasPendingRequest = Boolean(pendingReq);
                      const scheduleConflict = conflictBySessionId.get(session.id);
                      const hasScheduleConflict = Boolean(scheduleConflict);
                      const canAssignHere =
                        assignMode && isAssignableSessionStatus(session.status);
                      const hasAssignees = Boolean(
                        session.assignedColleagues && session.assignedColleagues.length > 0
                      );
                      const dimmedInAssign =
                        assignMode &&
                        (!canAssignHere || (onlyUnassigned && hasAssignees));
                      const isSelected = selectedSessionIds.includes(session.id);
                      const conflictTooltip = hasScheduleConflict
                        ? scheduleConflict!.assignees
                            .map(
                              (a) =>
                                `${a.firstName} ${a.lastName}: ${scheduleStatusLabel(
                                  a.scheduleStatus,
                                  a.scheduleApproved
                                )}`
                            )
                            .join('; ')
                        : '';
                      return (
                      <Tooltip
                        key={session.id}
                        title={
                          hasScheduleConflict
                            ? `Darbo grafiko konfliktas — ${conflictTooltip}`
                            : ''
                        }
                        disableHoverListener={!hasScheduleConflict}
                        enterDelay={250}
                      >
                      <Button
                        size="small"
                        disabled={assignMode && dimmedInAssign}
                        onClick={() => {
                          if (assignMode) {
                            if (!dimmedInAssign) toggleSessionSelect(session);
                            return;
                          }
                          if (canManage && pendingReq) {
                            openRequestReview(pendingReq);
                            return;
                          }
                          openEdit(session);
                        }}
                        sx={{
                          justifyContent: tallTechnical ? 'center' : 'flex-start',
                          alignItems: tallTechnical ? 'center' : 'stretch',
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          lineHeight: 1.2,
                          bgcolor: sessionTileBg(session, programs),
                          color: TOKENS.ink,
                          border: isSelected
                            ? `2px solid ${TOKENS.accent}`
                            : hasScheduleConflict
                              ? `2px solid ${TOKENS.conflictBorder}`
                              : hasPendingRequest
                                ? PENDING_REQUEST_CHIP_SX.border
                                : `1px solid ${TOKENS.line}`,
                          boxShadow: hasScheduleConflict
                            ? `0 0 0 2px rgba(255, 40, 160, 0.35)`
                            : hasPendingRequest && !isSelected
                              ? PENDING_REQUEST_CHIP_SX.boxShadow
                              : 'none',
                          borderRadius: 1,
                          opacity:
                            session.status === 'not_working'
                              ? 0.55
                              : dimmedInAssign
                                ? 0.38
                                : 1,
                          px: 0.7,
                          py: tallTechnical ? 1 : 0.35,
                          minHeight: tallTechnical ? FULL_DAY_TECH_TILE_MIN_HEIGHT : 0,
                          '&.Mui-disabled': {
                            color: TOKENS.ink,
                            bgcolor: sessionTileBg(session, programs)
                          },
                          '&:hover': {
                            bgcolor: sessionTileBg(session, programs),
                            borderColor: isSelected
                              ? TOKENS.accent
                              : hasScheduleConflict
                                ? TOKENS.conflictBorder
                                : hasPendingRequest
                                  ? 'rgba(255, 196, 77, 1)'
                                  : 'rgba(234,242,255,0.28)',
                            filter: dimmedInAssign ? 'none' : 'brightness(1.08)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            width: '100%',
                            gap: 0.25
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.45,
                              width: '100%',
                              minWidth: 0
                            }}
                          >
                            {canAssignHere && !dimmedInAssign && (
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                                sx={{
                                  p: 0,
                                  mr: 0.15,
                                  color: TOKENS.line,
                                  '&.Mui-checked': { color: TOKENS.accent },
                                  '& .MuiSvgIcon-root': { fontSize: 16 }
                                }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.72rem',
                                lineHeight: 1.15,
                                color: TOKENS.ink,
                                fontWeight: 600,
                                textAlign: 'left',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {session.status === 'not_working' ||
                              session.status === 'technical_day' ||
                              session.status === 'planetarium_closed'
                                ? `${statusLabel(session.status)}${
                                    isAllDayTimes(session.startTime, session.endTime)
                                      ? ' · Visa diena'
                                      : ''
                                  }`
                                : `${timeShort(session.startTime)} ${session.program || '—'}`}
                            </Typography>
                            {hasPendingRequest && (
                              <Box
                                component="span"
                                sx={{
                                  flexShrink: 0,
                                  px: 0.45,
                                  py: 0.1,
                                  borderRadius: 0.75,
                                  fontSize: '0.58rem',
                                  fontWeight: 800,
                                  letterSpacing: '0.03em',
                                  lineHeight: 1.2,
                                  color: '#041820',
                                  bgcolor: 'rgba(255, 190, 100, 0.9)'
                                }}
                              >
                                Prašymas
                              </Box>
                            )}
                            {hasScheduleConflict && (
                              <Box
                                component="span"
                                sx={{
                                  flexShrink: 0,
                                  px: 0.45,
                                  py: 0.1,
                                  borderRadius: 0.75,
                                  fontSize: '0.58rem',
                                  fontWeight: 800,
                                  letterSpacing: '0.03em',
                                  lineHeight: 1.2,
                                  color: '#fff',
                                  bgcolor: TOKENS.conflictInk
                                }}
                              >
                                Grafikas!
                              </Box>
                            )}
                            {!hasPendingRequest && !hasScheduleConflict && PRIVATE_STATUS_CHIP[session.status] && (
                              <Box
                                component="span"
                                sx={{
                                  flexShrink: 0,
                                  px: 0.45,
                                  py: 0.1,
                                  borderRadius: 0.75,
                                  fontSize: '0.58rem',
                                  fontWeight: 800,
                                  letterSpacing: '0.03em',
                                  lineHeight: 1.2,
                                  color: PRIVATE_STATUS_CHIP[session.status]!.color,
                                  bgcolor: PRIVATE_STATUS_CHIP[session.status]!.bgcolor
                                }}
                              >
                                {PRIVATE_STATUS_CHIP[session.status]!.label}
                              </Box>
                            )}
                            {session.language &&
                              session.status !== 'not_working' &&
                              session.status !== 'technical_day' &&
                              session.status !== 'planetarium_closed' && (
                                <Box
                                  component="span"
                                  sx={{
                                    flexShrink: 0,
                                    px: 0.45,
                                    py: 0.1,
                                    borderRadius: 0.75,
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    lineHeight: 1.2,
                                    color: TOKENS.ink,
                                    bgcolor: 'rgba(0, 0, 0, 0.35)',
                                    border: `1px solid ${TOKENS.line}`
                                  }}
                                >
                                  {String(session.language).toUpperCase()}
                                </Box>
                              )}
                          </Box>
                          {session.status !== 'not_working' &&
                            session.status !== 'technical_day' &&
                            session.status !== 'planetarium_closed' &&
                            assigneesLabel(session) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.66rem',
                                  lineHeight: 1.1,
                                  color: hasScheduleConflict ? TOKENS.conflictInk : TOKENS.muted,
                                  fontWeight: hasScheduleConflict ? 700 : 400,
                                  textAlign: 'left',
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {assigneesLabel(session)}
                              </Typography>
                            )}
                        </Box>
                      </Button>
                      </Tooltip>
                    );
                    })}
                    {daySessions.length > 5 && (
                      <Typography
                        variant="caption"
                        sx={{ color: TOKENS.muted, fontWeight: 600, pl: 0.25 }}
                      >
                        +{daySessions.length - 5} daugiau
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
              const dayTooltip = isUnderstaffed
                ? 'Šią dieną grafike nėra dirbančio planetariumo darbuotojo (statusas „Dirba“).'
                : hasAssigneeConflict
                  ? 'Šią dieną yra seansų, kurių priskirtas darbuotojas nėra „Dirba“ Darbo grafike.'
                  : '';
              return dayTooltip ? (
                <Tooltip
                  key={key}
                  title={dayTooltip}
                  enterDelay={350}
                  describeChild
                >
                  <Box sx={{ height: '100%', minHeight: '100%' }}>{dayCell}</Box>
                </Tooltip>
              ) : (
                <Box key={key} sx={{ height: '100%', minHeight: '100%' }}>
                  {dayCell}
                </Box>
              );
            })}
          </Box>
        </Paper>
          </>
        )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>{form.id ? 'Redaguoti seansą' : 'Naujas seansas'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {form.id && conflictBySessionId.has(form.id) && (
              <Alert
                severity="error"
                icon={<AssigneeConflictIcon />}
                sx={{
                  bgcolor: TOKENS.conflictFill,
                  border: `2px solid ${TOKENS.conflictBorder}`,
                  color: TOKENS.conflictInk,
                  '& .MuiAlert-icon': { color: TOKENS.conflictInk }
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.5 }}>
                  Priskyrimo konfliktas su Darbo grafiku
                </Typography>
                {conflictBySessionId.get(form.id)!.assignees.map((a) => (
                  <Typography key={a.userId} sx={{ fontSize: '0.8rem' }}>
                    {a.firstName} {a.lastName} — {scheduleStatusLabel(a.scheduleStatus, a.scheduleApproved)}
                  </Typography>
                ))}
              </Alert>
            )}
            <TextField
              label="Data"
              type="date"
              value={form.sessionDate}
              onChange={(e) => setForm((f) => ({ ...f, sessionDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={!canManage}
              fullWidth
            />
            <FormControl fullWidth disabled={!canEditRequestDraftFields}>
              <InputLabel>Statusas</InputLabel>
              <Select
                label="Statusas"
                value={form.status}
                onChange={(e) => {
                  const status = e.target.value as SessionStatus;
                  setForm((f) => {
                    if (isDayBlockStatus(status)) {
                      return {
                        ...f,
                        status,
                        allDay: true,
                        startTime: ALL_DAY_START,
                        endTime: ALL_DAY_END
                      };
                    }
                    return {
                      ...f,
                      status,
                      allDay: false,
                      startTime: f.allDay ? '11:00' : f.startTime,
                      endTime: f.allDay ? '11:45' : f.endTime
                    };
                  });
                }}
                MenuProps={{ PaperProps: { sx: MENU_PAPER_SX } }}
              >
                {statusOptionsForForm.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isDayBlockStatus(form.status) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.allDay}
                    disabled={!canManage}
                    onChange={(e) => {
                      const allDay = e.target.checked;
                      setForm((f) => ({
                        ...f,
                        allDay,
                        startTime: allDay ? ALL_DAY_START : f.startTime === ALL_DAY_START ? '11:00' : f.startTime,
                        endTime: allDay ? ALL_DAY_END : f.endTime === ALL_DAY_END ? '11:45' : f.endTime
                      }));
                    }}
                  />
                }
                label="Visa diena"
              />
            )}
            {!(isDayBlockStatus(form.status) && form.allDay) && (
              <Stack direction="row" spacing={2}>
                <TimeInput
                  label="Laikas"
                  value={form.startTime}
                  onChange={(startTime) => setForm((f) => ({ ...f, startTime, allDay: false }))}
                  disabled={!canManage}
                  fullWidth
                />
                <TimeInput
                  label="Pabaiga"
                  value={form.endTime}
                  onChange={(endTime) => setForm((f) => ({ ...f, endTime, allDay: false }))}
                  disabled={!canManage}
                  fullWidth
                />
              </Stack>
            )}
            <Autocomplete
              freeSolo
              options={activeProgramNames}
              value={form.program}
              onInputChange={(_, value) => setForm((f) => ({ ...f, program: value }))}
              disabled={!canManage}
              slotProps={{ paper: { sx: MENU_PAPER_SX } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Programa"
                  helperText={canManage ? 'Pasirinkite iš sąrašo arba įrašykite naują' : undefined}
                />
              )}
            />
            <TextField
              label="Kam skirta"
              value={form.audience}
              onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
              disabled={!canEditRequestDraftFields}
              fullWidth
            />
            <TextField
              label="Seanso kalba"
              value={form.language}
              onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
              disabled={!canEditRequestDraftFields}
              fullWidth
              helperText="Numatytoji: LT"
            />
            <TextField
              label="Užsakovų kontaktai"
              value={form.clientContacts}
              onChange={(e) => setForm((f) => ({ ...f, clientContacts: e.target.value }))}
              disabled={!canManage}
              fullWidth
              multiline
              minRows={2}
            />
            {canAssign && (
              <Box>
                {!fromStaffPool && canManage && (
                  <Alert
                    severity="info"
                    sx={{
                      ...ALERT_BASE_SX,
                      mb: 1,
                      bgcolor: 'rgba(61, 184, 197, 0.14)',
                      border: `1px solid rgba(61, 184, 197, 0.4)`,
                      '& .MuiAlert-icon': { color: TOKENS.accent }
                    }}
                  >
                    Darbuotojų grupė tuščia — rodomi visi aktyvūs vartotojai. Geriau nustatykite
                    grupę per „Darbuotojai“.
                  </Alert>
                )}
                <Autocomplete
                  multiple
                  options={colleagues}
                  value={selectedAssignees}
                  onChange={(_, value) =>
                    setForm((f) => ({ ...f, assigneeIds: value.map((v) => v.id) }))
                  }
                  getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  slotProps={{ paper: { sx: MENU_PAPER_SX } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Atsakingas asmuo" placeholder="Pasirinkite" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={`${option.firstName} ${option.lastName}`}
                        {...getTagProps({ index })}
                        key={option.id}
                        size="small"
                      />
                    ))
                  }
                />
              </Box>
            )}
            <TextField
              label="Komentarai"
              value={form.comments}
              onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
              disabled={!canManage}
              fullWidth
              multiline
              minRows={2}
            />
            {canRequestPrivate && form.id && baselineIsPlanned && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${TOKENS.warnBorder}`,
                  bgcolor: TOKENS.warnFill
                }}
              >
                <Typography variant="subtitle2" sx={{ color: TOKENS.ink, mb: 1, fontWeight: 700 }}>
                  Prašyti privataus seanso
                </Typography>
                {pendingRequestBySessionId.has(form.id) ? (
                  <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                    Šiam seansui jau pateiktas laukiantis prašymas.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                      Įmonė ar grupė nori užimti šį atvirą laiką kaip privatų seansą. Pasirinkite
                      statusą aukščiau (Planuojamas Privatus arba Patvirtintas Privatus).
                      Administratorius gali patvirtinti arba atmesti.
                    </Typography>
                    <TextField
                      label="Užsakovų kontaktai"
                      value={requestContacts}
                      onChange={(e) => setRequestContacts(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Komentarai / detalės"
                      value={requestComments}
                      onChange={(e) => setRequestComments(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Stack>
                )}
              </Box>
            )}
            {canRequestPrivate && form.id && baselineIsRequestable && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid rgba(61, 184, 197, 0.45)`,
                    bgcolor: 'rgba(61, 184, 197, 0.12)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: TOKENS.ink, mb: 1, fontWeight: 700 }}>
                    Keisti seansą
                  </Typography>
                  {pendingRequestBySessionId.has(form.id) ? (
                    <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                      Šiam seansui jau pateiktas laukiantis prašymas.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                        Dabartinė programa: {requestBaseline?.program || '—'}. Galite pakeisti
                        programą čia ir/arba statusą, kalbą ar „Kam skirta“ aukščiau.
                      </Typography>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={activeProgramNames}
                        value={requestedProgram}
                        onInputChange={(_, value) => setRequestedProgram(value)}
                        slotProps={{ paper: { sx: MENU_PAPER_SX } }}
                        renderInput={(params) => (
                          <TextField {...params} label="Norima programa" />
                        )}
                      />
                      <TextField
                        label="Komentaras (neprivaloma)"
                        value={requestComments}
                        onChange={(e) => setRequestComments(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Stack>
                  )}
                </Box>
              )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {canManage && form.id && (
            <Button
              onClick={deleteSession}
              disabled={saving}
              sx={{
                ...BTN_GHOST_SX,
                color: '#c03939',
                borderColor: TOKENS.dangerBorder,
                bgcolor: TOKENS.dangerFill,
                '&:hover': { bgcolor: 'rgba(255, 90, 90, 0.28)', borderColor: TOKENS.dangerBorder }
              }}
              variant="outlined"
            >
              Ištrinti
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDialogOpen(false)} sx={BTN_GHOST_SX} variant="outlined">
            Uždaryti
          </Button>
          {canRequestPrivate &&
            form.id &&
            baselineIsPlanned &&
            !pendingRequestBySessionId.has(form.id) && (
              <Button
                variant="contained"
                onClick={submitPrivateRequest}
                disabled={submittingRequest}
                sx={BTN_PRIMARY_SX}
              >
                {submittingRequest ? 'Siunčiama…' : 'Prašyti privataus'}
              </Button>
            )}
          {canRequestPrivate &&
            form.id &&
            baselineIsRequestable &&
            !pendingRequestBySessionId.has(form.id) && (
              <Button
                variant="outlined"
                onClick={submitProgramChangeRequest}
                disabled={submittingRequest || !hasSessionChangeDraft}
                sx={BTN_GHOST_SX}
              >
                {submittingRequest ? 'Siunčiama…' : 'Prašyti pakeitimo'}
              </Button>
            )}
          {canManage && (
            <Button variant="contained" onClick={saveSession} disabled={saving} sx={BTN_PRIMARY_SX}>
              Išsaugoti
            </Button>
          )}
          {!canManage && canAssign && form.id && (
            <Button variant="contained" onClick={saveSession} disabled={saving} sx={BTN_PRIMARY_SX}>
              Išsaugoti atsakingus
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={programsDialogOpen}
        onClose={() => setProgramsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>Programos</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1, color: TOKENS.muted }}>
            Administruojamas programų sąrašas seanso formai. Pasirinkite spalvą kalendoriaus
            plytelėms. Vienkartines programas vis dar galima įrašyti laisvai seanso dialoge.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems="center">
            <TextField
              size="small"
              fullWidth
              label="Nauja programa"
              value={newProgramName}
              onChange={(e) => setNewProgramName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void addProgram();
                }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: TOKENS.muted }}>
                Spalva
              </Typography>
              <Box
                component="input"
                type="color"
                value={newProgramColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewProgramColor(e.target.value)
                }
                sx={{
                  width: 40,
                  height: 36,
                  border: `1px solid ${TOKENS.line}`,
                  borderRadius: 1,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  p: 0.25
                }}
              />
            </Box>
            <Button
              variant="contained"
              onClick={addProgram}
              disabled={savingProgram || !newProgramName.trim()}
              sx={BTN_PRIMARY_SX}
            >
              Pridėti
            </Button>
          </Stack>
          <List dense>
            {programs.map((p) => (
              <ListItem
                key={p.id}
                sx={{
                  opacity: p.isActive ? 1 : 0.5,
                  borderRadius: 1,
                  border: `1px solid ${TOKENS.line}`,
                  bgcolor: 'rgba(17, 17, 17, 0.04)',
                  mb: 0.75,
                  pr: 10
                }}
              >
                <Box
                  component="input"
                  type="color"
                  value={p.color && /^#[0-9A-Fa-f]{6}$/.test(p.color) ? p.color : '#90caf9'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    void updateProgramColor(p.id, e.target.value)
                  }
                  disabled={!p.isActive}
                  sx={{
                    width: 32,
                    height: 32,
                    border: `1px solid ${TOKENS.line}`,
                    borderRadius: 1,
                    bgcolor: 'transparent',
                    cursor: p.isActive ? 'pointer' : 'default',
                    p: 0.2,
                    mr: 1.25,
                    flexShrink: 0
                  }}
                />
                <ListItemText
                  primary={p.name}
                  secondary={p.isActive ? null : 'Išjungta'}
                />
                <ListItemSecondaryAction>
                  {p.isActive ? (
                    <IconButton edge="end" aria-label="delete" onClick={() => deactivateProgram(p.id)}>
                      <DeleteIcon />
                    </IconButton>
                  ) : (
                    <Button size="small" onClick={() => reactivateProgram(p.id)} sx={BTN_GHOST_SX} variant="outlined">
                      Atkurti
                    </Button>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {programs.length === 0 && (
              <Typography variant="body2" sx={{ px: 2, color: TOKENS.muted }}>
                Dar nėra programų — pridėkite pirmąją.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgramsDialogOpen(false)} variant="outlined" sx={BTN_GHOST_SX}>
            Uždaryti
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reviewRequest)}
        onClose={closeRequestReview}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>Peržiūrėti prašymą</DialogTitle>
        <DialogContent>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                ...ALERT_BASE_SX,
                mt: 1,
                mb: 0,
                bgcolor: TOKENS.dangerFill,
                border: `1px solid ${TOKENS.dangerBorder}`,
                '& .MuiAlert-icon': { color: '#c03939' }
              }}
            >
              {error}
            </Alert>
          )}
          {reviewRequest &&
            (() => {
              const req = reviewRequest;
              const type = (req.requestType || 'private_existing') as RequestType;
              const sid = req.sessionId || req.session?.id;
              const currentSession =
                (sid && sessions.find((s) => s.id === sid)) || req.session || null;
              const requesterName = req.requester
                ? `${req.requester.firstName} ${req.requester.lastName}`
                : '—';
              const panelSx = {
                flex: 1,
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${TOKENS.line}`,
                bgcolor: 'rgba(17, 17, 17, 0.04)',
                minWidth: 0
              } as const;
              return (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={REQUEST_TYPE_LABELS[type]}
                      sx={{
                        ...CHIP_BASE_SX,
                        bgcolor:
                          type === 'program_change'
                            ? 'rgba(61, 184, 197, 0.25)'
                            : 'rgba(255, 196, 77, 0.25)',
                        borderColor:
                          type === 'program_change'
                            ? 'rgba(61, 184, 197, 0.5)'
                            : 'rgba(255, 196, 77, 0.55)',
                        color: TOKENS.ink,
                        fontWeight: 700
                      }}
                    />
                    <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                      Prašo: {requesterName}
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                    <Box sx={panelSx}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          color: TOKENS.muted,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Dabar
                      </Typography>
                      {type === 'private_new' || !currentSession ? (
                        <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                          Nėra seanso
                        </Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          <Typography sx={{ color: TOKENS.ink, fontWeight: 700 }}>
                            {String(currentSession.sessionDate).slice(0, 10)} ·{' '}
                            {timeShort(currentSession.startTime)}
                            {currentSession.endTime
                              ? `–${timeShort(currentSession.endTime)}`
                              : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                            Programa: {currentSession.program || '—'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                            Statusas: {statusLabel(currentSession.status as SessionStatus)}
                          </Typography>
                          {currentSession.clientContacts && (
                            <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                              Kontaktai: {currentSession.clientContacts}
                            </Typography>
                          )}
                          {currentSession.comments && (
                            <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                              Komentarai: {currentSession.comments}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Box>
                    <Box
                      sx={{
                        ...panelSx,
                        border: PENDING_REQUEST_CHIP_SX.border,
                        boxShadow: PENDING_REQUEST_CHIP_SX.boxShadow,
                        bgcolor: 'rgba(255, 196, 77, 0.12)'
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          color: TOKENS.muted,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Prašoma
                      </Typography>
                      <Stack spacing={0.75}>
                        {type === 'private_existing' && (
                          <>
                            <Typography sx={{ color: TOKENS.ink, fontWeight: 700 }}>
                              {currentSession
                                ? `${String(currentSession.sessionDate).slice(0, 10)} · ${timeShort(
                                    currentSession.startTime
                                  )}`
                                : '—'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                              Statusas →{' '}
                              {statusLabel(
                                (req.requestedStatus || 'confirmed_private') as SessionStatus
                              )}
                            </Typography>
                            <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                              Programa: {currentSession?.program || req.program || '—'}
                            </Typography>
                            {req.requestedLanguage && (
                              <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                                Kalba → {req.requestedLanguage}
                              </Typography>
                            )}
                            {req.requestedAudience != null && req.requestedAudience !== '' && (
                              <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                                Kam skirta → {req.requestedAudience}
                              </Typography>
                            )}
                          </>
                        )}
                        {type === 'private_new' && (
                          <>
                            <Typography sx={{ color: TOKENS.ink, fontWeight: 700 }}>
                              {String(req.sessionDate || '').slice(0, 10)} · {timeShort(req.startTime)}
                              {req.endTime ? `–${timeShort(req.endTime)}` : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                              Naujas seansas · Patvirtintas Privatus
                            </Typography>
                            <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                              Programa: {req.program || '—'}
                            </Typography>
                          </>
                        )}
                        {type === 'program_change' && (
                          <>
                            <Typography sx={{ color: TOKENS.ink, fontWeight: 700 }}>
                              {currentSession
                                ? `${String(currentSession.sessionDate).slice(0, 10)} · ${timeShort(
                                    currentSession.startTime
                                  )}`
                                : '—'}
                            </Typography>
                            {req.requestedProgram && (
                              <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                                Programa: {currentSession?.program || req.program || '—'} →{' '}
                                <Box component="span" sx={{ fontWeight: 800, color: TOKENS.accent }}>
                                  {req.requestedProgram}
                                </Box>
                              </Typography>
                            )}
                            {req.requestedStatus && (
                              <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                                Statusas:{' '}
                                {currentSession?.status
                                  ? statusLabel(currentSession.status as SessionStatus)
                                  : '—'}{' '}
                                → {statusLabel(req.requestedStatus as SessionStatus)}
                              </Typography>
                            )}
                            {req.requestedLanguage && (
                              <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                                Kalba: {currentSession?.language || '—'} → {req.requestedLanguage}
                              </Typography>
                            )}
                            {req.requestedAudience != null && req.requestedAudience !== '' && (
                              <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                                Kam skirta → {req.requestedAudience}
                              </Typography>
                            )}
                            {!req.requestedProgram &&
                              !req.requestedStatus &&
                              !req.requestedLanguage &&
                              (req.requestedAudience == null || req.requestedAudience === '') && (
                                <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                                  —
                                </Typography>
                              )}
                          </>
                        )}
                        {req.clientContacts && (
                          <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                            Kontaktai: {req.clientContacts}
                          </Typography>
                        )}
                        {req.comments && (
                          <Typography variant="body2" sx={{ color: TOKENS.ink }}>
                            Komentaras: {req.comments}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRequestReview} variant="outlined" sx={BTN_GHOST_SX}>
            Uždaryti
          </Button>
          <Box sx={{ flex: 1 }} />
          {reviewRequest && (
            <>
              <Button
                variant="outlined"
                disabled={reviewingRequestId === reviewRequest.id}
                onClick={() => denyPrivateRequest(reviewRequest.id)}
                sx={{
                  ...BTN_GHOST_SX,
                  color: '#c03939',
                  borderColor: TOKENS.dangerBorder
                }}
              >
                Atmesti
              </Button>
              <Button
                variant="contained"
                disabled={reviewingRequestId === reviewRequest.id}
                onClick={() => acceptPrivateRequest(reviewRequest.id)}
                sx={BTN_PRIMARY_SX}
              >
                Priimti
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={privateRequestsOpen}
        onClose={() => setPrivateRequestsOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>Prašymai</DialogTitle>
        <DialogContent>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                ...ALERT_BASE_SX,
                mt: 1,
                mb: 1,
                bgcolor: TOKENS.dangerFill,
                border: `1px solid ${TOKENS.dangerBorder}`,
                '& .MuiAlert-icon': { color: '#c03939' }
              }}
            >
              {error}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2, mt: 1, color: TOKENS.muted }}>
            Privatus: atviras seansas tampa prašomu privačiu statusu. Naujas laikas: sukuriamas naujas
            privatus seansas. Seansas: atnaujinama programa, statusas, kalba ir/arba „Kam skirta“.
          </Typography>
          <Stack spacing={1.25}>
            {privateRequests.filter((r) => r.status === 'pending').length === 0 && (
              <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                Nėra laukiančių prašymų šiame mėnesyje.
              </Typography>
            )}
            {privateRequests
              .filter((r) => r.status === 'pending')
              .map((req) => {
                const sess = req.session;
                const type = (req.requestType || 'private_existing') as RequestType;
                const dateLabel = String(
                  (sess && sess.sessionDate) || req.sessionDate || ''
                ).slice(0, 10);
                const timeLabel = timeShort((sess && sess.startTime) || req.startTime);
                const programLabel =
                  type === 'program_change'
                    ? req.requestedProgram
                      ? `${sess?.program || req.program || '—'} → ${req.requestedProgram}`
                      : sess?.program || req.program || '—'
                    : sess?.program || req.program || '—';
                const changeBits: string[] = [];
                if (type === 'program_change') {
                  if (req.requestedStatus) {
                    changeBits.push(`statusas → ${statusLabel(req.requestedStatus as SessionStatus)}`);
                  }
                  if (req.requestedLanguage) changeBits.push(`kalba → ${req.requestedLanguage}`);
                  if (req.requestedAudience) changeBits.push('kam skirta');
                } else if (type === 'private_existing' && req.requestedStatus) {
                  changeBits.push(
                    `statusas → ${statusLabel(req.requestedStatus as SessionStatus)}`
                  );
                }
                const requesterName = req.requester
                  ? `${req.requester.firstName} ${req.requester.lastName}`
                  : '—';
                return (
                  <Box
                    key={req.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${TOKENS.line}`,
                      bgcolor: 'rgba(17, 17, 17, 0.04)'
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      justifyContent="space-between"
                      alignItems={{ sm: 'flex-start' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={REQUEST_TYPE_LABELS[type]}
                            sx={{
                              ...CHIP_BASE_SX,
                              bgcolor:
                                type === 'program_change'
                                  ? 'rgba(61, 184, 197, 0.25)'
                                  : 'rgba(255, 196, 77, 0.25)',
                              borderColor:
                                type === 'program_change'
                                  ? 'rgba(61, 184, 197, 0.5)'
                                  : 'rgba(255, 196, 77, 0.55)',
                              color: TOKENS.ink,
                              fontWeight: 700
                            }}
                          />
                          <Typography sx={{ color: TOKENS.ink, fontWeight: 700 }}>
                            {dateLabel || '—'} · {timeLabel || '—'} · {programLabel}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: TOKENS.muted, mt: 0.35 }}>
                          Prašo: {requesterName}
                          {changeBits.length > 0 ? ` · ${changeBits.join(', ')}` : ''}
                        </Typography>
                        {req.clientContacts && (
                          <Typography variant="body2" sx={{ color: TOKENS.ink, mt: 0.75 }}>
                            Kontaktai: {req.clientContacts}
                          </Typography>
                        )}
                        {req.comments && (
                          <Typography variant="body2" sx={{ color: TOKENS.ink, mt: 0.5 }}>
                            Komentaras: {req.comments}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} flexShrink={0}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={reviewingRequestId === req.id}
                          onClick={() => acceptPrivateRequest(req.id)}
                          sx={BTN_PRIMARY_SX}
                        >
                          Priimti
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={reviewingRequestId === req.id}
                          onClick={() => denyPrivateRequest(req.id)}
                          sx={{
                            ...BTN_GHOST_SX,
                            color: '#c03939',
                            borderColor: TOKENS.dangerBorder
                          }}
                        >
                          Atmesti
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivateRequestsOpen(false)} variant="outlined" sx={BTN_GHOST_SX}>
            Uždaryti
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newPrivateOpen}
        onClose={() => setNewPrivateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>Prašyti naujo privataus seanso</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: TOKENS.muted }}>
              Kai nėra atviro seanso norimu laiku (pvz. 20:00), pateikite naują prašymą.
              Patvirtinus bus sukurtas Patvirtintas Privatus seansas.
            </Typography>
            <TextField
              label="Data"
              type="date"
              value={newPrivateForm.sessionDate}
              onChange={(e) =>
                setNewPrivateForm((f) => ({ ...f, sessionDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TimeInput
                label="Pradžia"
                value={newPrivateForm.startTime}
                onChange={(startTime) => setNewPrivateForm((f) => ({ ...f, startTime }))}
                fullWidth
              />
              <TimeInput
                label="Pabaiga"
                value={newPrivateForm.endTime}
                onChange={(endTime) => setNewPrivateForm((f) => ({ ...f, endTime }))}
                fullWidth
              />
            </Stack>
            <Autocomplete
              freeSolo
              options={activeProgramNames}
              value={newPrivateForm.program}
              onInputChange={(_, value) => setNewPrivateForm((f) => ({ ...f, program: value }))}
              slotProps={{ paper: { sx: MENU_PAPER_SX } }}
              renderInput={(params) => <TextField {...params} label="Programa" />}
            />
            <TextField
              label="Užsakovų kontaktai"
              value={newPrivateForm.clientContacts}
              onChange={(e) =>
                setNewPrivateForm((f) => ({ ...f, clientContacts: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Komentarai / detalės"
              value={newPrivateForm.comments}
              onChange={(e) => setNewPrivateForm((f) => ({ ...f, comments: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewPrivateOpen(false)} variant="outlined" sx={BTN_GHOST_SX}>
            Atšaukti
          </Button>
          <Button
            variant="contained"
            onClick={submitNewPrivateRequest}
            disabled={submittingRequest}
            sx={BTN_PRIMARY_SX}
          >
            {submittingRequest ? 'Siunčiama…' : 'Pateikti prašymą'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={staffDialogOpen}
        onClose={() => setStaffDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>Planetariumo darbuotojai</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1, color: TOKENS.muted }}>
            Ši grupė naudojama seansų priskyrimui ir įspėjimui, kai dieną nėra nė vieno grupės
            nario su statusu „Dirba“ grafike (Nuotoliu neįskaitomas).
          </Typography>
          <Autocomplete
            multiple
            options={allUsers}
            value={staffSelected}
            onChange={(_, value) => setStaffPoolIds(value.map((v) => v.id))}
            getOptionLabel={(o) =>
              `${o.firstName} ${o.lastName}${o.department ? ` (${o.department})` : ''}`
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            slotProps={{ paper: { sx: MENU_PAPER_SX } }}
            renderInput={(params) => (
              <TextField {...params} label="Nariai" placeholder="Pridėti darbuotoją" />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <FormControlLabel
                  control={<Checkbox checked={staffPoolIds.includes(option.id)} size="small" />}
                  label={`${option.firstName} ${option.lastName}`}
                />
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffDialogOpen(false)} variant="outlined" sx={BTN_GHOST_SX}>
            Atšaukti
          </Button>
          <Button variant="contained" onClick={saveStaffPool} disabled={savingPool} sx={BTN_PRIMARY_SX}>
            Išsaugoti
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={weeklyPlanOpen}
        onClose={() => {
          saveWeeklyPlan(weeklyPlan);
          setWeeklyPlanOpen(false);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>Savaitės planas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, mt: 1, color: TOKENS.muted }}>
            Pasirinkite dieną ir nustatykite jos seansus (arba Techninę dieną). Tuščios dienos
            nepildomos. Tada užpildykite{' '}
            <Box component="span" sx={{ color: TOKENS.ink, fontWeight: 600 }}>
              {formatMonthYearLt(currentMonth)}
            </Box>
            . Esami seansai tuo pačiu laiku neperrašomi.
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              color: TOKENS.muted,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            Dienos
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            {WEEKDAY_OPTIONS.map((d) => {
              const isActive = weeklyPlan.activeWeekday === d.value;
              const daySlots = weeklyPlan.byWeekday[String(d.value)] || [];
              const count = daySlots.length;
              const hasSlots = count > 0;
              const isTechOnly =
                hasSlots && daySlots.every((s) => s.status === 'technical_day');
              const isClosedOnly =
                hasSlots && daySlots.every((s) => s.status === 'planetarium_closed');
              return (
                <Button
                  key={d.value}
                  size="small"
                  onClick={() => setActiveWeeklyWeekday(d.value)}
                  variant={isActive ? 'contained' : 'outlined'}
                  aria-pressed={isActive}
                  sx={{
                    minWidth: 52,
                    height: 36,
                    px: 1.25,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    letterSpacing: '0.02em',
                    borderRadius: 1.5,
                    borderWidth: 2,
                    borderStyle: 'solid',
                    ...(isActive
                      ? {
                          bgcolor: 'rgba(17, 17, 17, 0.08)',
                          color: '#041820',
                          borderColor: TOKENS.accent,
                          boxShadow: `0 0 0 3px rgba(61, 184, 197, 0.45)`,
                          '&:hover': {
                            bgcolor: '#ffffff',
                            borderColor: TOKENS.accent,
                            boxShadow: `0 0 0 3px rgba(61, 184, 197, 0.55)`
                          }
                        }
                      : isTechOnly
                        ? {
                            bgcolor: 'rgba(255, 220, 120, 0.2)',
                            color: TOKENS.ink,
                            borderColor: 'rgba(255, 220, 120, 0.45)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 220, 120, 0.3)',
                              borderColor: 'rgba(255, 220, 120, 0.6)'
                            }
                          }
                        : isClosedOnly
                          ? {
                              bgcolor: 'rgba(110, 125, 155, 0.22)',
                              color: TOKENS.ink,
                              borderColor: 'rgba(110, 125, 155, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(110, 125, 155, 0.32)',
                                borderColor: 'rgba(110, 125, 155, 0.65)'
                              }
                            }
                          : hasSlots
                            ? {
                                bgcolor: 'rgba(61, 184, 197, 0.14)',
                                color: TOKENS.ink,
                                borderColor: 'rgba(61, 184, 197, 0.4)',
                                '&:hover': {
                                  bgcolor: 'rgba(61, 184, 197, 0.24)',
                                  borderColor: 'rgba(61, 184, 197, 0.55)'
                                }
                              }
                            : {
                                bgcolor: 'transparent',
                                color: TOKENS.muted,
                                borderColor: TOKENS.line,
                                '&:hover': {
                                  bgcolor: 'rgba(17, 17, 17, 0.06)',
                                  borderColor: 'rgba(234,242,255,0.35)',
                                  color: TOKENS.ink
                                }
                              })
                  }}
                >
                  {isTechOnly ? `${d.label} · TD` : hasSlots ? `${d.label} · ${count}` : d.label}
                </Button>
              );
            })}
          </Stack>
          <Typography variant="body2" sx={{ mb: 2, color: TOKENS.ink, fontWeight: 600 }}>
            Redaguojama:{' '}
            <Box component="span" sx={{ color: TOKENS.accent }}>
              {
                (
                  {
                    1: 'Pirmadienis',
                    2: 'Antradienis',
                    3: 'Trečiadienis',
                    4: 'Ketvirtadienis',
                    5: 'Penktadienis',
                    6: 'Šeštadienis',
                    7: 'Sekmadienis'
                  } as Record<number, string>
                )[weeklyPlan.activeWeekday]
              }
            </Box>
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              color: TOKENS.muted,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            {WEEKDAY_OPTIONS.find((d) => d.value === weeklyPlan.activeWeekday)?.label || ''} —
            seansai / blokai
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 1.5 }}>
            {activeDaySlots(weeklyPlan).length === 0 && (
              <Typography variant="body2" sx={{ color: TOKENS.muted }}>
                Ši diena tuščia — pridėkite laikus, Techninę dieną, Planetariumas uždarytas, arba
                palikite tuščią (nebus pildoma).
              </Typography>
            )}
            {activeDaySlots(weeklyPlan).map((slot) =>
              slot.status === 'technical_day' || slot.status === 'planetarium_closed' ? (
                <Box
                  key={slot.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    border:
                      slot.status === 'technical_day'
                        ? `1px solid rgba(255, 220, 120, 0.45)`
                        : `1px solid rgba(110, 125, 155, 0.5)`,
                    bgcolor:
                      slot.status === 'technical_day'
                        ? 'rgba(255, 220, 120, 0.18)'
                        : 'rgba(110, 125, 155, 0.22)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1
                  }}
                >
                  <Typography variant="body2" sx={{ color: TOKENS.ink, fontWeight: 700 }}>
                    {slot.status === 'technical_day'
                      ? 'Techninė diena · Visa diena'
                      : 'Planetariumas uždarytas · Visa diena'}
                  </Typography>
                  <IconButton
                    aria-label="Pašalinti"
                    onClick={() => removeWeeklySlot(slot.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  key={slot.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    border: `1px solid ${TOKENS.line}`,
                    bgcolor: 'rgba(17, 17, 17, 0.04)'
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'flex-start' }}
                  >
                    <TimeInput
                      label="Pradžia"
                      size="small"
                      value={slot.startTime}
                      onChange={(startTime) => updateWeeklySlot(slot.id, { startTime })}
                      sx={{ minWidth: { sm: 130 } }}
                    />
                    <TextField
                      label="Trukmė (min)"
                      type="number"
                      size="small"
                      value={slot.durationMinutes}
                      onChange={(e) =>
                        updateWeeklySlot(slot.id, {
                          durationMinutes: Math.max(
                            5,
                            Math.min(12 * 60, Number(e.target.value) || 60)
                          )
                        })
                      }
                      inputProps={{ min: 5, max: 720, step: 5 }}
                      sx={{ minWidth: { sm: 120 } }}
                    />
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={activeProgramNames}
                      value={slot.program}
                      onInputChange={(_, value) => updateWeeklySlot(slot.id, { program: value })}
                      slotProps={{ paper: { sx: MENU_PAPER_SX } }}
                      sx={{ flex: 1, minWidth: { xs: '100%', sm: 160 } }}
                      renderInput={(params) => <TextField {...params} label="Programa" />}
                    />
                    <TextField
                      label="Kalba"
                      size="small"
                      value={slot.language}
                      onChange={(e) => updateWeeklySlot(slot.id, { language: e.target.value })}
                      sx={{ minWidth: { sm: 90 }, maxWidth: { sm: 110 } }}
                    />
                    <IconButton
                      aria-label="Pašalinti"
                      onClick={() => removeWeeklySlot(slot.id)}
                      sx={{ mt: { sm: 0.5 } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Box>
              )
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addWeeklySlot}
              variant="outlined"
              sx={BTN_GHOST_SX}
            >
              Pridėti laiką
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addWeeklyTechnicalDay}
              variant="outlined"
              disabled={activeDaySlots(weeklyPlan).some((s) => s.status === 'technical_day')}
              sx={{
                ...BTN_GHOST_SX,
                borderColor: 'rgba(255, 220, 120, 0.45)',
                color: TOKENS.ink,
                '&:hover': {
                  borderColor: 'rgba(255, 220, 120, 0.7)',
                  bgcolor: 'rgba(255, 220, 120, 0.12)'
                }
              }}
            >
              Techninė diena
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addWeeklyPlanetariumClosed}
              variant="outlined"
              disabled={activeDaySlots(weeklyPlan).some((s) => s.status === 'planetarium_closed')}
              sx={{
                ...BTN_GHOST_SX,
                borderColor: 'rgba(110, 125, 155, 0.5)',
                color: TOKENS.ink,
                '&:hover': {
                  borderColor: 'rgba(110, 125, 155, 0.75)',
                  bgcolor: 'rgba(110, 125, 155, 0.14)'
                }
              }}
            >
              Planetariumas uždarytas
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              saveWeeklyPlan(weeklyPlan);
              setWeeklyPlanOpen(false);
            }}
            variant="outlined"
            sx={BTN_GHOST_SX}
          >
            Uždaryti
          </Button>
          <Button
            variant="contained"
            onClick={populateMonthFromWeeklyPlan}
            disabled={populatingMonth || !weeklyPlanHasSlots(weeklyPlan)}
            sx={BTN_PRIMARY_SX}
          >
            {populatingMonth ? 'Pildoma…' : 'Užpildyti mėnesį'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(clearDayDate)}
        onClose={() => {
          if (!pastingDay) setClearDayDate(null);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
        sx={DIALOG_SX}
      >
        <DialogTitle>
          {t('planetarium.clearDayConfirmTitle', 'Ištrinti dienos seansus')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: TOKENS.ink, pt: 0.5 }}>
            {t(
              'planetarium.clearDayConfirmBody',
              'Ištrinti visus šios dienos seansus ({{date}})? Šio veiksmo atšaukti negalima.',
              { date: clearDayDate }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearDayDate(null)}
            disabled={pastingDay}
            variant="outlined"
            sx={BTN_GHOST_SX}
          >
            {t('common.cancel', 'Atšaukti')}
          </Button>
          <Button
            onClick={clearDayPlan}
            disabled={pastingDay}
            variant="contained"
            sx={{
              ...BTN_PRIMARY_SX,
              bgcolor: '#d64545',
              color: '#fff',
              '&:hover': { bgcolor: '#c03939' }
            }}
          >
            {pastingDay ? t('planetarium.clearingDay', 'Trinama…') : t('planetarium.clearDayConfirm', 'Ištrinti')}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default Planetariumas;
